// Construction tracking against the model itself.
//
// The drawing view answers "where is this run and how far along is it".
// This one answers "what is in the model, and what has the field done to
// it" - every element, in the model's own hierarchy, coloured where it
// stands, over the drawing it belongs to.
//
// The parts are deliberately one screen: the tree is where work is
// marked, the model is where it is seen, and the numbers are what gets
// reported. Splitting them would let the three disagree about the same
// question, which is how a board stops being believed.
import { alignmentForPage } from "./twoPointAlign";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Input, Modal, Progress, Select, Spin, Switch, Tabs, Tag, Tooltip, message } from "antd";
import { popupInStage, stageContainer } from "./stagePopup";
import {
  DeleteOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined, PlusOutlined, PrinterOutlined, ScheduleOutlined,
} from "@ant-design/icons";
import BulkBar from "./BulkBar";
import { forServer, fromSaved, rulesInWords } from "./quantityHeaderFilters";
import SelectionDetails from "./SelectionDetails";
import AreaTag from "./AreaTag";
import PartCategoryPicker from "./PartCategoryPicker";
import CustomCategoryList from "./CustomCategoryList";
import FragmentIsolation from "webgl/js/loader/streaming/isolationRule";
import { expandCompact } from "helpers/treeRowUids";
import { resolveToElement } from "webgl/js/loader/streaming/nestedParts";
import { complementRule, hideEverythingRule } from "webgl/js/loader/streaming/filterVisibility";
import { asFragmentRule } from "webgl/js/loader/streaming/isolationRule";
import { useTrackingScope } from "./useTrackingScope";
import { scopeApplyFrom } from "./scopeApplyState";
import ScopeApplyBar from "./ScopeApplyBar";
import ElementTree from "./ElementTree";
import ModelStage from "./ModelStage";
import NewSincePublish from "./NewSincePublish";
import QuantityTree from "./QuantityTree";
import { keysForElement, mergeCheckedKeys, minimalKeys, nextSelection } from "./scopeKeys";
import { exclusionKeys, minimalRecordKeys, nodesForKeys, pruneExclusions, recordKeyWords } from "./recordKeys";
import { ruleForSelection, sourceKeys } from "./selectionUids";
import { trace } from "./selectionTrace";
import { visibleDocuments, filterLabel } from "./visibleDocuments";
import { usePreference } from "helpers/userPrefs";
import { extrasOf } from "./cutExtras";
import { elevationAsk, taggedOnlyFrom, cutUidsFor, askFailedRows } from "./exportElevation";
import { definitionFrom, refuseDefinition } from "./partCategoryRule";
import { clearLevelFilter } from "./cutClearsFilter";
import { composeLayout, levelOrder } from "./exportLayouts";
import { getStatusTotals, getTrackedBuckets } from "api/tracking/trackingExportApi";
import { dateForStatus, paintMap } from "./stageHelpers";
import { announceStatus, needsConfirmation, setStatusWithPreview } from "./statusBulk";
import { undoElementStatus } from "api/tracking/trackingElementsApi";
import { STATUS_COLOR, STATUS_LABEL, STATUS_ORDER, STATUS_SHORT } from "./trackingTheme";
import {
  createList, createPage, deleteList, deletePage, exportCsv, exportPdf, exportXlsx, getElement, getSelectionSummary,
  getElementStats, getLevels, getLists, getModelDocuments, getPages, getScope, getScopeUids, getSelectionLevelUids, getFilterUids, createQuantitySelection, getPagesCutUids, getAssemblyGroups, createDerivedCategory,
  saveScope, setElementStatus, syncToSchedule, updatePage, getElementList, openPdf, getScopePreview,
  getDerivedCategories, getDerivedCategoryUids,
} from "api/tracking/trackingElementsApi";

const T = { line: "#EFEFEF", muted: "#8D8D8D", ink: "#23343E" };

// "category:Cable Trays" -> "Cable Trays"; a type key names its type.
function parseKeyLabel(key) {
  const raw = String(key || "");
  const at = raw.indexOf(":");
  if (at < 0) return "";
  const parts = raw.slice(at + 1).split("|");
  return parts[parts.length - 1] || parts[0] || "";
}

// The charts, drawn onto a canvas rather than captured off the page.
//
// The export needs a picture of the numbers, and screen-scraping the DOM
// needs a library this app does not carry. Drawing them here is a few
// lines, is deterministic, and cannot disagree with the numbers it was
// given - which a screenshot of a chart mid-animation can.
function renderStatsChart(stats, { width = 1400, height = 420 } = {}) {
  if (!stats || !stats.overall) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  const byStatus = stats.overall.byStatus || {};
  const total = Math.max(1, stats.overall.total || 0);

  // One stacked bar: the whole scope, in status order.
  ctx.font = "600 20px system-ui, sans-serif";
  ctx.fillStyle = "#23343E";
  ctx.fillText(`${stats.overall.total || 0} elements · ${stats.overall.percentInstalled || 0}% installed`, 40, 44);

  let x = 40;
  const barW = width - 80;
  const barY = 70;
  for (const s of STATUS_ORDER) {
    const w = ((byStatus[s] || 0) / total) * barW;
    if (w <= 0) continue;
    ctx.fillStyle = STATUS_COLOR[s];
    ctx.fillRect(x, barY, w, 44);
    x += w;
  }

  // The key, under it.
  x = 40;
  ctx.font = "14px system-ui, sans-serif";
  for (const s of STATUS_ORDER) {
    ctx.fillStyle = STATUS_COLOR[s];
    ctx.fillRect(x, barY + 62, 14, 14);
    ctx.fillStyle = "#23343E";
    const label = `${STATUS_LABEL[s]} ${byStatus[s] || 0}`;
    ctx.fillText(label, x + 20, barY + 74);
    x += 40 + ctx.measureText(label).width;
  }

  // By category, so the picture says which trade is behind rather than
  // only how far the job is.
  const cats = (stats.categories || []).slice(0, 12);
  if (cats.length) {
    const top = barY + 110;
    const rowH = Math.min(24, (height - top - 30) / cats.length);
    ctx.font = "13px system-ui, sans-serif";
    cats.forEach((c, i) => {
      const y = top + i * rowH;
      ctx.fillStyle = "#6B7280";
      ctx.fillText(String(c.category).slice(0, 28), 40, y + rowH - 8);
      const rowX = 300;
      const rowW = width - 340;
      let cx = rowX;
      for (const s of STATUS_ORDER) {
        const w = ((c.byStatus?.[s] || 0) / Math.max(1, c.total)) * rowW;
        if (w <= 0) continue;
        ctx.fillStyle = STATUS_COLOR[s];
        ctx.fillRect(cx, y + 4, w, rowH - 10);
        cx += w;
      }
      ctx.fillStyle = "#6B7280";
      ctx.fillText(`${c.percentInstalled}%`, width - 34, y + rowH - 8);
    });
  }
  return canvas.toDataURL("image/png");
}

// The rules behind the ticks live in scopeKeys.js, shared with the
// tests; minimalKeys is re-exported so nothing that imported it from
// here has to move.
export { minimalKeys };

// THE "Show only filtered" SENTENCE, OUTSIDE THE COMPONENT ON PURPOSE.
// eslint-plugin-react-hooks counts code paths through the whole component;
// three inline ternaries in this file's JSX overflow its limit and surface as
// 29 false "React Hook called conditionally" errors at unrelated hooks, which
// fails the build (2026-09-19, GlanceTab, same cause). So the branching lives
// here and the JSX reads one value.
const TOGGLE_BG = { on: "#FFFDF5", off: "transparent" };

// WHICH ELEMENTS ARE LIT. Owner, 2026-09-28: "why is filtering blocks the
// selection in the 3d and forces the level 1 items to be highlighted? the
// request was to filter fragments, and not to highlight with filter".
//
// selectionUids used to add the FILTER'S OWN MATCH, so filtering by Level 1 lit
// every Level 1 element and a click was one glow among tens of thousands - the
// filter had taken the selection's colour. filterUids serves two jobs though:
// the filter's match AND rows ticked inside it, both from the same server call.
// Ticked rows are a selection INSIDE the set and keep their colour; the bare
// filter gets none. That is the innerKeys distinction.
// Out here because this component is one branch from overflowing
// eslint-plugin-react-hooks' path counter.
/**
 * WHAT TO HIDE so the viewer holds nothing the table has already excluded.
 *
 * OUT HERE FOR THE REASON ticksAreLit IS: this component is one branch from
 * overflowing eslint-plugin-react-hooks' path counter, and two ifs inside the
 * useMemo tipped it into 34 "hook called conditionally" errors about hooks
 * nowhere near them. The remedy was already written in this file.
 *
 * TICKED ROWS NOW HIDE THE REST (owner, 2026-10-05: "automatically hide
 * unchecked and out of filter fragments"). THIS REVERSES HIS OWN RULING of
 * 2026-09-20 - "ticking a row in Tracking quantities colours elements without
 * changing which ones are drawn" - and that older note is left where it is,
 * because it explains why the code looked as it did and deleting it would
 * make this change look arbitrary to the next reader.
 *
 * TICKS WIN OVER THE FILTER when both are on: a tick is a selection INSIDE
 * the filter, so hiding down to the ticks already hides what the filter
 * excluded. Asking for both is the same answer twice.
 *
 * `filteredOnly` GOVERNS BOTH, which is what keeps it usable - one switch
 * that puts the whole model back. Without it, ticking a row would hide
 * everything else with no way out but unticking the row you wanted to see.
 */
// WHY THE MODEL LOOKS LIKE THAT, in words, for the two states a person
// cannot otherwise account for.
//
// A BLANK VIEWER MUST EXPLAIN ITSELF AND SAY THE WAY OUT. Obeying "nothing
// ticked" literally is what he asked for, but an empty 3D view with no
// sentence is the broken-page reading the old rule was protecting against -
// the fix for that is the sentence, not refusing his instruction.
//
// AND A TICK THAT MATCHED NOTHING IS THE OTHER ONE. complementRule refuses a
// count of 0, so the model is left exactly as it was - correct, and silent,
// which is what made this look broken in the first place.
//
// Out here with its siblings for the hooks path counter.
function hideNoteFor({ filteredOnly, innerKeys, innerRule, quantityTouched, quantityFilters }) {
  if (!filteredOnly) return "";
  if (quantityTouched && !innerKeys.length && !quantityFilters) {
    return "Nothing is ticked in Tracking quantities, so nothing is shown. Tick a row, or turn off \"Select filtered only\" to see the model again.";
  }
  if (innerKeys.length && innerRule) {
    const n = innerRule.elementCount
      || (innerRule.singles ? innerRule.singles.length : 0)
      || (innerRule.hashes ? innerRule.hashes.length : 0);
    if (!n) return "Those ticks match nothing in the published model, so the view is left as it was.";
  }
  return "";
}

function hideOutside({ filteredOnly, innerKeys, innerRule, quantityFilters, filterUids, quantityTouched }) {
  if (!filteredOnly) return null;
  // UNTICKED EVERYTHING, HAVING TICKED SOMETHING: show nothing. The owner's
  // ruling of 2026-10-05 - the view obeys the ticks literally once he has
  // touched them, zero included. A PRISTINE page falls through to the end and
  // shows everything, which is why the flag is the fact of interaction and not
  // the length of the list.
  if (quantityTouched && !innerKeys.length && !quantityFilters) {
    return hideEverythingRule('nothing is ticked in Tracking quantities');
  }
  if (innerKeys.length && innerRule) {
    const n = innerRule.elementCount
      || (innerRule.singles ? innerRule.singles.length : 0)
      || (innerRule.hashes ? innerRule.hashes.length : 0);
    // complementRule refuses a count of 0, and that refusal is the point: a
    // tick resolving to nothing leaves the model alone rather than hiding all
    // of it to represent "no results".
    return complementRule(asFragmentRule(innerRule), { count: n });
  }
  if (!quantityFilters || !filterUids.length) return null;
  return complementRule(new FragmentIsolation({ singles: filterUids }), { count: filterUids.length });
}

// A FILTER ON ITS OWN NOW LIGHTS ITS MATCH (owner, 2026-10-05: the filter or
// the checkbox "should select all fragments of those checked/filtered
// categories in the 3D view").
//
// The note under litUids records why the match was taken OUT of the selection
// colour: "filtering by Level 1 lit every Level 1 element and a click was one
// glow among tens of thousands". That rested on the rest of the model still
// being drawn, and hideOutside above means it is not - everything visible IS
// the match. The two changes are what make each other safe.
//
// The WHOLE condition lives here, and not half of it: an inline
// `||` in the memo was enough on its own to tip the hooks path counter.
// The WHOLE condition, including the or - not just half of it: an inline
// `||` in the memo was enough on its own to tip the hooks path counter.
const litNow = (litByTicks, quantityFilters, levelUids) => (
  litByTicks || (!!quantityFilters && !levelUids.length)
);

function ticksAreLit(quantityFilters, levelUids, innerKeys) {
  if (!quantityFilters) return false;
  if (levelUids.length) return false;
  return innerKeys.length > 0;
}
function litUids(selected, levelUids, filterUids, litByTicks) {
  const out = [...(selected || []), ...levelUids];
  if (litByTicks) out.push(...filterUids);
  return [...new Set(out)];
}

// CRUD SPLIT (2026-10-14): canEdit stays the management switch (status,
// scope, pages, schedule sync); adding and removing pages, sets and custom
// categories follow their own keys. Helpers out here, not ternaries in the
// component: the body is big enough that one more branch trips the hooks
// lint's path limit.
const HIDDEN = { display: "none" };
const shownIf = (on) => (on ? undefined : HIDDEN);
const allOf = (...xs) => xs.every(Boolean);
const whenAllowed = (on, fn) => (on ? fn : null);

export default function ModelTracking({
  projectId, project, sheets = [], canEdit = false, activities = [], statsPrefix = null,
  // The drawing cards beside the board (TrackingPage) read again after a write.
  onStatusWritten = null,
  canAdd = canEdit, canDelete = canEdit, canAddSet = true,
}) {
  const [documents, setDocuments] = useState([]);
  // What the user picked, as the endpoint's own document ids.
  const [documentIds, setDocumentIds] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [activeList, setActiveList] = useState("");
  const [checked, setChecked] = useState([]);
  // The Detailed tree's ticks, in ITS keys (recordKeys.js: rc: / rg:).
  // Kept apart from `checked` for one reason: `checked` is also the
  // project's tracking SCOPE, which is saved in the commit tree's words,
  // and a record key in it would be saved as a scope nobody can resolve.
  // Everything else - the viewer's selection, the bulk bar, the isolate -
  // treats the two as one selection.
  const [recordChecked, setRecordChecked] = useState([]);
  // HAS HE TOUCHED THE QUANTITIES TICKS ON THIS VISIT?
  //
  // Owner's ruling, 2026-10-05, through the leader: a PRISTINE page shows
  // everything - the tab has to open usable, and isolating nothing on open
  // reads as a broken page. Once he has touched the ticks the view obeys them
  // literally, INCLUDING ZERO: unticking everything means he wants nothing
  // shown.
  //
  // So the thing carried is the FACT OF INTERACTION, not the length of the
  // list. "never touched + empty" and "touched + empty" are the two states
  // that look identical and mean opposite things, and a count cannot tell
  // them apart - the same shape as the empty saved scope meaning "not chosen
  // yet" and derivedIds empty meaning "all of them". A DELIBERATE ACT
  // OUTRANKS A DERIVED ONE: an empty selection he created by unticking is a
  // decision; an empty one that is merely the initial state is not.
  //
  // NOT PERSISTED, deliberately. Per page visit. If it survived a reload he
  // would open the page tomorrow to an empty viewer because of something he
  // did today - the broken-page problem coming back through the side door.
  const [quantityTouched, setQuantityTouched] = useState(false);
  // WHAT WAS DROPPED OUT of a ticked branch: "this category except that
  // one" (owner, 2026-09-20). Kept beside the ticks rather than inside
  // them, because the tick still means the whole branch - the exclusions
  // are what is taken off it, and every number the page shows is
  // branch-minus-exclusions.
  const [excluded, setExcluded] = useState([]);
  // What a status change is dated. Defaults to today; the field marks
  // last week's work as often as today's, so it is one control for the
  // panel, the tree and the bulk actions rather than a question each time.
  const [statusDate, setStatusDate] = useState(() => new Date().toISOString().slice(0, 10));
  // When a PROJECTED install is expected. Its own field: a projection is
  // dated ahead, and the "as of" date above is capped at today.
  const [projectedDate, setProjectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  // What the model should frame and flash: the elements a tick in the
  // tree or a choice in "new since publish" just named.
  const [focus, setFocus] = useState({ uids: null, key: 0 });
  const focusOn = useCallback((uids) => {
    if (!uids || !uids.length) return;
    setFocus((prev) => ({ uids, key: prev.key + 1 }));
  }, []);
  // Elements gathered in the model view with CTRL-clicks, for a
  // bulk status change or to be put into the tracked scope together.
  const [selected, setSelected] = useState([]);
  // THE BOX (owner, 2026-09-22): what a ctrl + drag in empty space caught,
  // stored on the server so Tracking quantities can show only it.
  // { id, count } | null. Emptying the selection ends it.
  const [boxSel, setBoxSel] = useState(null);
  useEffect(() => { if (!selected.length) setBoxSel(null); }, [selected.length]);
  const [bulkStatus, setBulkStatus] = useState("installed");
  const [bulkBusy, setBulkBusy] = useState(false);
  // A scope of a million elements is not resolved to ids just to paint
  // it; past this size the ids are fetched only when the stage's "Only
  // tracked" switch asks for them.
  // "Only new in model": the ids the last publish added, when that
  // switch is on; null otherwise. Composes with a page's cut below.
  const [newFocus, setNewFocus] = useState(null);
  // The pages this project tracks on. One per level, usually: each level
  // has its own drawing, so a page is a sheet, a slice of the model, and
  // the alignment between them.
  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = useState("");
  const [levels, setLevels] = useState([]);
  // Declared right below the page state, ABOVE everything that reads it
  // (2026-09-22: the selection, the stats and the trees now read the page's
  // levels too). Declared above the first thing that reads it. It used to sit a
  // hundred lines below `cutLevels`, which is a temporal dead zone: the
  // component threw ReferenceError on its very first render, and with no
  // error boundary above the board React unmounted the whole tree - the
  // Model tab took the page white (2026-09-09).
  const activePage = useMemo(
    () => pages.find((x) => String(x._id) === String(activePageId)) || null,
    [pages, activePageId]
  );

  const cutLevels = useMemo(() => {
    const c = activePage && activePage.cut;
    return c && c.mode === "level" ? (c.levelNames || []) : [];
  }, [activePage]);
  // THE PAGE'S LEVELS FILTER EVERYTHING ON IT (owner, 2026-09-22: "each page
  // should have the option to only count and filter elements by levels
  // selection, could 1 or many, then all tracked/selected elements get
  // filtered by that level"): both trees, the board, every selection and
  // every status write. The viewer already isolated to them.
  const cutLevelsKey = cutLevels.join("|");
  // "Also show whatever is at this elevation", and how far above and
  // below "at" reaches - saved on the page like its levels are, because
  // what a page DRAWS has to reopen the way it was left.
  const atElevation = !!(activePage && activePage.cut && activePage.cut.mode === "level" && activePage.cut.atElevation);
  // The second, separate population: no level tag at all. See
  // utils/levelElevation - BA03's Generic Models all carry "".
  const untaggedAtElevation = !!(activePage && activePage.cut && activePage.cut.mode === "level" && activePage.cut.untaggedAtElevation);
  // TWO REACHES (owner, 2026-10-02: a range, not a set value).
  const elevBelow = (activePage && activePage.cut && activePage.cut.elevBelow != null) ? Number(activePage.cut.elevBelow) : 2;
  const elevAbove = (activePage && activePage.cut && activePage.cut.elevAbove != null) ? Number(activePage.cut.elevAbove) : 2;
  const [elevationInfo, setElevationInfo] = useState({ note: "", added: 0, range: null });
  // THE ELEVATION HALF OF THE CUT, AS AN ID THE SERVER CAN RESOLVE.
  //
  // The uids the elevation switches added have to reach the quantities
  // table, the stats, the totals and the status WRITE - not only the
  // viewer's isolate, which is the bug this fixes: the model drew 5,557
  // elements the table could not count and the write could not touch.
  // They travel as a selection id rather than a list because 5,557 Revit
  // UniqueIds is ~260 KB on EVERY one of those calls.
  const [cutUidsId, setCutUidsId] = useState("");

  const [listOpen, setListOpen] = useState(false);
  // The custom-part-category picker, opened from the bulk bar.
  const [partsOpen, setPartsOpen] = useState(false);
  // Bumped after a create, so the list under the picker shows what was just
  // made rather than the answer from before it existed.
  const [partsMade, setPartsMade] = useState(0);
  // WHICH CUSTOM CATEGORY THE CURRENT SELECTION'S PARTS CAME FROM.
  //
  // A part (a beam clamp inside a ceiling strut) has no commit row, so the
  // server cannot tell a part id from an element id that a re-export
  // dropped - and searching for it costs a full scan of the part rows
  // (measured on the live commit: 10.5 s for 40 ids). The selection came
  // FROM a definition, so the page carries the definition's id and the
  // server resolves it in one indexed read.
  //
  // CLEARED BY ANY OTHER KIND OF SELECTION, below, because a stale id here
  // would tell the server that somebody else's elements are this category's
  // parts.
  const [partsFrom, setPartsFrom] = useState("");
  // A list made from "New since last publish" is a set of ELEMENTS: the
  // diff is redefined by the next publish, so there is no pick to remember
  // that would still mean the same thing.
  const [listSeed, setListSeed] = useState([]);
  const [listName, setListName] = useState("");
  const [exporting, setExporting] = useState("");
  // uid -> status, for the elements somebody has touched. This is what
  // colours the model. Untouched elements are not in it and are not
  // painted: a colour for every element in the model to say nobody has
  // started is a quarter of a million entries asserting nothing, and the
  // viewer already draws them in their own material.
  const [statusByUid, setStatusByUid] = useState(new Map());
  // The tracked scope: what the tree's ticks mean. Loaded from the
  // project, saved back when the owner changes it, and resolved to the
  // ids the model paints.
  // THE TICKED CUSTOM CATEGORIES (owner, 2026-10-04: "they don't have
  // their check box"). Their own state, never mixed into scopeKeys: that
  // array is model node keys and it is handed to the viewer's scope
  // filter and the preview counts, neither of which can resolve a
  // definition. null until the tree reports, so the autosave can tell
  // "not loaded yet" from "all unticked".
  const [derivedIds, setDerivedIds] = useState(null);
  const [scope, setScope] = useState(null);
  const [scopeUids, setScopeUids] = useState([]);
  const [scopeSaving, setScopeSaving] = useState(false);
  // Has GET /scope answered at all - either way? See the board's gate.
  const [scopeLoaded, setScopeLoaded] = useState(false);
  // Ticks made by a person in this tab, as opposed to the saved pick
  // arriving: only the former is written back.
  const userTouched = useRef(false);
  const saveTimer = useRef(null);
  // Set by ModelStage so the export can take the picture the user is
  // looking at without this component reaching into the viewer.
  const stage = useRef({ captureRef: null, save: null });

  // The saved pick seeds the tree. Nothing saved reads as nothing ticked,
  // which the tree shows as the whole model uncoloured.
  useEffect(() => {
    let alive = true;
    if (!projectId) return undefined;
    setScopeLoaded(false);
    getScope(projectId)
      .then((r) => {
        if (!alive) return;
        setScope(r.scope || null);
        if (r.scope && Array.isArray(r.scope.derivedIds)) setDerivedIds(r.scope.derivedIds.map(String));
        if (!userTouched.current) setChecked((r.scope && r.scope.nodeKeys) || []);
      })
      .catch(() => { if (alive) setScope(null); })
      // ANSWERED, whichever way. `scope` cannot carry this: null is both
      // "no saved scope" and "the call failed", and the board must not
      // wait for ever on a project that simply has no scope.
      .finally(() => { if (alive) setScopeLoaded(true); });
    return () => { alive = false; };
  }, [projectId]);

  // The scope's keys, reduced to the sections that carry them.
  const scopeKeys = useMemo(() => minimalKeys(checked), [checked]);
  const scopeKeysCsv = useMemo(() => scopeKeys.join(","), [scopeKeys]);
  // CUSTOM PART CATEGORIES (owner, 2026-10-02). Three thin handlers: the
  // model, the endpoints and the create dialog all already exist (shipped
  // 2026-09-24) and none of them is reimplemented here.
  //
  // WHY THE CREATE BUTTON HAS NEVER WORKED UNTIL NOW: AssemblyGroups
  // computes `canCreate = !!(onCreate && totalsNote)` and nothing in the
  // codebase passed either - it shipped inert on purpose, because HAL
  // would not have a button that could double-count silently. The rule is
  // now written down in partCategoryRule, so the button can exist.
  const loadPartGroups = useCallback(
    (params) => getAssemblyGroups(projectId, params),
    [projectId],
  );
  // THE SAME CODE PATH PRICES IT AND CREATES IT: dryRun runs every check
  // and every sum and writes nothing, so the dialog can show the movement
  // while it is still a decision. A preview computed differently from the
  // real thing is a preview that can be wrong and nobody finds out.
  const previewPartCategory = useCallback(
    (def) => createDerivedCategory(projectId, { ...definitionFrom(def), dryRun: true }),
    [projectId],
  );
  const createPartCategory = useCallback(async (def) => {
    const d = definitionFrom(def);
    const refusal = refuseDefinition(d);
    // REFUSED HERE AS WELL AS ON THE SERVER, because the server would
    // ACCEPT a definition with no source category and count every part in
    // the model - a number nobody could explain.
    if (refusal) { message.error(refusal); throw new Error(refusal); }
    const r = await createDerivedCategory(projectId, d);
    message.success(`"${d.name}" now counts ${d.part.family || d.part.category || "the parts"} inside ${d.source.category}`);
    return r;
  }, [projectId]);
  // THE CATEGORIES THE DROPDOWN OFFERS: the ones the owner has TICKED,
  // read from the scope keys the page already holds. Not a second request,
  // and not a tree fetch - "all tracked categories" is literally what
  // `category:…` keys in the scope are.
  //
  // I FIRST WROTE THIS AGAINST A `scopeTree` THAT DOES NOT EXIST. It
  // would have been an empty dropdown with no error - the shape of fault
  // this file's own history is full of - so the source is now a value I
  // checked rather than one I assumed.
  //
  // WITH NOTHING TICKED the dropdown falls back to the categories the
  // board has numbers for, because a project whose scope is empty means
  // "not chosen yet" and not "no categories".
  const trackedCategories = useMemo(() => {
    const fromScope = (scopeKeys || [])
      .filter((k) => String(k).startsWith("category:"))
      .map((k) => ({ name: String(k).slice("category:".length) }));
    if (fromScope.length) return fromScope;
    const byCat = (stats && stats.byCategory) || [];
    return byCat
      .map((c) => ({ name: c.category || c.name || c._id, count: c.total }))
      .filter((c) => c.name);
  }, [scopeKeys, stats]);

  // The linked files, from the same endpoint the model files gear reads,
  // so "what is in this model" has one answer rather than two.
  //
  // WITHOUT THE SWITCHED-OFF FILES (owner, 2026-09-20: "does the source
  // button in construction tracking respect the links unselected in the
  // project card config?"). It did not. This took r.documents whole,
  // so the picker offered - and "N of M linked files" counted - files
  // the card has switched off, whose geometry is deliberately NOT DRAWN:
  // every streaming viewer drops their rows at tile ingest. Picking one
  // gave rows and counts for a file nobody can see, which is the list
  // and the stream disagreeing about what the model is.
  //
  // `switchedOff` rather than the `hidden` flag, because the server
  // already combines BOTH card controls into it - the Model files gear
  // AND the placement dialog's visible:false - and on SpaceX BA03 every
  // one of the thirteen is hidden by the PLACEMENT dialog, with
  // modelSourceFilter null. Filtering on `hidden` alone would have
  // looked right in code and changed nothing on the project that
  // prompted it.
  useEffect(() => {
    let alive = true;
    if (!projectId) return undefined;
    getModelDocuments(projectId)
      .then((r) => { if (alive) setDocuments(visibleDocuments(r)); })
      .catch(() => { if (alive) setDocuments([]); });
    return () => { alive = false; };
  }, [projectId]);

  // The filter the SERVER understands.
  //
  // An element is attributed to a linked file by the first 36 characters
  // of its uniqueId - the document's own GUID - which is how the model
  // files gear does it without the exporter tagging anything. The
  // endpoint's `id` is the exporter's DocumentId, a different string
  // entirely, so filtering by it matched nothing at all: picking a file
  // emptied the tree. The prefixes are what to send.
  const prefixFilter = useMemo(() => {
    if (!documentIds.length) return "";
    const picked = new Set(documentIds.map(String));
    const prefixes = [];
    documents.forEach((d) => {
      if (!picked.has(String(d.id))) return;
      (d.prefixes || []).forEach((p) => { if (p) prefixes.push(p); });
    });
    return [...new Set(prefixes)].join(",");
  }, [documentIds, documents]);

  // A file whose elements have no attribution yet cannot be filtered on,
  // and silently showing the whole model instead would be a lie about
  // what is on screen.
  const unattributed = useMemo(() => {
    if (!documentIds.length) return [];
    const picked = new Set(documentIds.map(String));
    return documents.filter((d) => picked.has(String(d.id)) && !(d.prefixes || []).length)
      .map((d) => d.name);
  }, [documentIds, documents]);

  // The numbers are a percentage of the SCOPE. A board that reports the
  // walls as not installed is not reporting the electrical work.
  // THE LATEST ASK WINS (owner, 2026-10-19: the stats "are not reflecting
  // real time changes"). A scoped read takes seconds; two writes in a row
  // let the first, older answer land last and put the old counts back.
  const statsSeq = useRef(0);
  const loadStats = useCallback(async () => {
    if (!projectId) return;
    const mine = (statsSeq.current += 1);
    setStatsLoading(true);
    try {
      const r = await getElementStats(projectId, {
        documentIds: prefixFilter,
        listId: activeList || undefined,
        nodeKeys: scopeKeysCsv || undefined,
        pageLevels: cutLevelsKey || undefined,
        cutUids: cutUidsId || undefined,
      });
      if (statsSeq.current === mine) setStats(r);
    } catch (e) {
      if (statsSeq.current === mine) message.error(e.message || "Could not read the tracking stats");
    } finally {
      if (statsSeq.current === mine) setStatsLoading(false);
    }
  }, [projectId, prefixFilter, activeList, scopeKeysCsv, cutLevelsKey, cutUidsId]);

  // THE BOARD WAITS FOR THE SCOPE, AND THE REASON IS CORRECTNESS BEFORE
  // IT IS SPEED.
  //
  // loadStats is keyed on the ticked keys, which are seeded from
  // GET /scope - so on mount it ran once with NO keys and again with
  // them. The first answer is the WHOLE MODEL's percentages, rendered on
  // the board for as long as the second call takes: a number that is not
  // this project's scope, on screen, being read as if it were.
  //
  // It is also the most expensive call on the page: unscoped it is a
  // median of 6,513 ms on prod against 2,979 ms for the scoped one that
  // replaces it - 2.2x the cost, discarded.
  //
  // Gated on scopeLoaded rather than on `scope` being non-null, because
  // a project with nothing saved would otherwise never load its board.
  useEffect(() => { if (scopeLoaded) loadStats(); }, [loadStats, scopeLoaded]);

  // Every touched element's status, slim: the id and the step, so a
  // whole project's rows fit one answer.
  const loadTracked = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await getElementList(projectId, {
        tracked: 1,
        slim: 1,
        // ASSEMBLY PART STATUSES TOO. They live in their own collection so
        // nothing picks them up by accident (models/trackedPart); this is
        // the one place that wants them, and it says so. Without it a part
        // marked installed would be a status with no colour anywhere.
        parts: 1,
        documentIds: prefixFilter,
        listId: activeList || undefined,
        limit: 400000,
      });
      const map = new Map();
      (r.elements || []).forEach((e) => map.set(e.uniqueId, e.status));
      setStatusByUid(map);
    } catch (e) {
      // The model still draws; it just carries no status colours.
    }
  }, [projectId, prefixFilter, activeList]);

  useEffect(() => { loadTracked(); }, [loadTracked]);

  // The ids the CURRENT ticks resolve to, from the server: ticking a
  // category does not mean expanding it to forty thousand rows in the
  // browser. Always the ticks on screen (never the saved scope's count,
  // which is the owner's and can be stale for anyone else), always
  // fetched, so "Only tracked" is instant whatever the scope's size.
  // Debounced, because a person ticking five categories in a row wants
  // one answer, not five.
  const [scopeResolving, setScopeResolving] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!projectId || !scopeKeys.length) { setScopeUids([]); setScopeResolving(false); return undefined; }
    setScopeResolving(true);
    const t = setTimeout(() => {
      getScopeUids(projectId, { nodeKeys: scopeKeysCsv, documentIds: prefixFilter })
        .then((r) => {
          if (!alive) return;
          setScopeUids(r.uids || []);
          if (r.capped) message.warning(`The scope is larger than ${(r.count || 0).toLocaleString()} elements; only the first of them are shown.`);
        })
        .catch(() => { if (alive) setScopeUids([]); })
        .finally(() => { if (alive) setScopeResolving(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [projectId, scopeKeysCsv, prefixFilter, scopeKeys.length]);

  // The owner's ticks are the project's scope, written back a moment
  // after the last one. Anyone else's ticks preview in this tab only.
  // Keyed on canEdit as well as on the keys: a tick made before GET
  // /scope had answered (thirty seconds on a slow box) used to find
  // `scope` still null, skip the save, and never be asked again.
  // DECLARED HERE, ABOVE THE AUTOSAVE THAT WRITES ONE OF THEM. A const
  // read above its own declaration is the temporal-dead-zone crash this
  // codebase has paid for twice; the autosave's callback would have
  // been fine, but "fine because of when the closure runs" is not a
  // thing to leave for the next reader to work out.
  const [savedNonce, setSavedNonce] = useState(0);
  const [applyNonce, setApplyNonce] = useState(0);
  const [scopePreview, setScopePreview] = useState(null);
  const onApplyScope = useCallback(() => setApplyNonce(savedNonce), [savedNonce]);

  const canEditScope = !!(scope && scope.canEdit);
  // Joined, not the array: a fresh array every render would re-run the
  // autosave for ever. Same reason as scopeKeysCsv above it.
  const derivedCsv = (derivedIds || []).join(",");

  useEffect(() => {
    if (!projectId || !userTouched.current || !canEditScope) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setScopeSaving(true);
      try {
        const r = await saveScope(projectId, {
          nodeKeys: scopeKeys,
          // Only once the tree has reported. Sending [] before it has
          // loaded would save "nothing tracked" over the saved set, and
          // on this page an empty set means something different from
          // nothing chosen - see the server's note.
          ...(derivedIds ? { derivedIds } : {}),
          documentIds: prefixFilter ? prefixFilter.split(",") : [],
        });
        setScope((prev) => ({ ...(prev || {}), ...(r.scope || {}) }));
        // THE DATA IS SAVED; THE VIEW IS NOT. Bumping this is what makes
        // the button's middle state true - "saved, not applied" - rather
        // than the autosave silently leaving the stage describing a
        // selection nobody has any more.
        setSavedNonce((v) => v + 1);
      } catch (e) {
        message.error(e.message || "Could not save what is tracked");
      } finally {
        setScopeSaving(false);
      }
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, scopeKeysCsv, prefixFilter, canEditScope, derivedCsv]);

  // What the model paints: every element with a status in its own step
  // (in the ticked scope or not), and the untouched rest of the scope in
  // `projected` - see stageHelpers.paintMap.
  const paintByUid = useMemo(() => paintMap(scopeUids, statusByUid), [scopeUids, statusByUid]);

  // What a page's cut narrows the model to. Only the cut: a tick in the
  // tree says "track this", never "hide the rest".
  const [isolateUids, setIsolateUids] = useState(null);
  // THE TICKED CUSTOM CATEGORIES' PART IDS, so the isolate can DRAW them
  // (owner, 2026-10-05: the 3D panels do not show after selection).
  //
  // "Tracked elements" isolates to drawKeys, which are Model elements node
  // keys resolved over CommitElement - and a PART has no CommitElement row,
  // so no node key can ever name one. With the switch on (its default) a
  // custom category's parts were therefore never in the drawn set: hidden,
  // the viewer drops raycast hits on hidden rows, the click resolved to
  // nothing and the panel never opened. In both views, because both read
  // the same panel.
  //
  // `null` means "not worked out", `[]` means "worked out, there are none" -
  // the difference decides whether the augment below is applied at all.
  const [derivedPartUids, setDerivedPartUids] = useState(null);
  const [derivedPartNote, setDerivedPartNote] = useState("");
  // THE PARTS OF A CUSTOM CATEGORY TICKED IN THE QUANTITIES TABLE, which
  // is a different question from the one above: those are made VISIBLE
  // inside the isolate because the tracked scope includes them, these are
  // PAINTED because somebody ticked them (owner, 2026-10-04: the filter or
  // check box "should select all fragments of those checked/filtered
  // categories in the 3D view"). They must also be visible, or the paint
  // lands on geometry nobody can see - so they join both rules.
  const [tickedPartUids, setTickedPartUids] = useState([]);
  const [tickedPartNote, setTickedPartNote] = useState("");
  const [isolating, setIsolating] = useState(false);

  // "Show only the selected elements" (owner, 2026-09-20). A view state,
  // remembered per person: the ticks (or, with none, what is picked in the
  // model) resolved to ids by the server and handed to the viewer's
  // isolation. A row too big to isolate refuses as a whole - the model is
  // left intact and the switch goes back off, because a half-isolated
  // model reads as a filtered one and is not.
  // ON by default (owner, 2026-09-20): arriving at the Model tab with
  // categories ticked shows those, not the whole building. Still remembered
  // per person, so anyone who turns it off keeps it off.
  const [onlySelectedPref, setOnlySelectedPref] = usePreference("tracking.onlySelected", true);
  // A TEMPORARY CONDITION MUST NEVER WRITE A REMEMBERED VALUE - pause it
  // for the visit, and let the control be its only writer. The shape of
  // the fault, not just this instance of it (leader, 2026-09-20): it will
  // recur wherever a refusal, an error or a takeover reaches for a
  // setter that happens to persist. Found here and on the sheet overlay
  // ("Align by 2 points" wrote it true) in the same hour.
  //
  // Three things used to switch this off by writing the REMEMBERED value - a selection too big to isolate, an
  // error working one out, and the viewer's own [I] taking over - so one
  // oversized tick turned the owner's default off for ever, on every
  // project, and nothing said it had. Those three now pause it for this
  // visit; the remembered value changes only when a person uses the
  // switch or the Isolate button.
  const [isolatePaused, setIsolatePaused] = useState(false);
  const onlySelected = onlySelectedPref && !isolatePaused;
  const setOnlySelected = useCallback((on) => { setIsolatePaused(false); setOnlySelectedPref(!!on); }, [setOnlySelectedPref]);
  const pauseOnlySelected = useCallback(() => setIsolatePaused(true), []);
  const [onlySelectedUids, setOnlySelectedUids] = useState(null);
  // A big row is isolated by RULE (fragment hashes) rather than by a list of
  // ids: root-f5's rule isolate, prod 2026-09-20. A category no longer has a
  // size that cannot be isolated.
  const [selectionRule, setSelectionRule] = useState(null);
  // The rule for what is DRAWN. Usually the very same object - a person
  // who only ticks in Model elements asks one question and gets one
  // answer - and a second, narrower resolve only when the two trees
  // disagree, which is the one case where the highlight is wider than the
  // isolate. Null means "isolate nothing", i.e. the whole model.
  const [drawRule, setDrawRule] = useState(null);
  // The rule for what is PAINTED inside the set - the Tracking quantities
  // ticks alone. Null when that tree has none.
  const [innerRule, setInnerRule] = useState(null);
  // A LEVEL PICKED IN "Selection details" (owner, 2026-09-20). It is a
  // HIGHLIGHT inside the drawn set and nothing else: it never reaches
  // drawKeys, so the isolate does not move and unticking a level cannot
  // make geometry vanish. The ids live here rather than in the panel
  // because the viewer and the quantities tree must see the same pick.
  // THE CHAIN THE OWNER ASKED FOR (2026-09-21): "I need the precise
  // filter to set the status" - filter precisely, select exactly what
  // matched, write to exactly that. The filter lives in the quantities
  // tree; the SELECTION lives here, because the count the bulk bar shows
  // and the set the write touches must be one thing.
  const [quantityFilters, setQuantityFilters] = useState(null);
  // THE TABLE'S SELECT-ALL: "Select all N elements this filter matched", the
  // checkbox in the quantities header. Off by default and cleared by a filter
  // change, because a select-all belongs to the filter it was made under.
  const [allFiltered, setAllFiltered] = useState(false);

  // "SELECT FILTERED ONLY" - a different question, and it used to share the
  // boolean above, which is why one switch had two meanings (owner, 2026-09-29:
  // "change the functionality of that toggle and name, to force only selection
  // of any or all filtered elements, as the user selects in 3D ... if its off
  // the user can select anything in the 3D view").
  //
  // ON (the default): the filter hides everything it did not match, and hidden
  // elements are not clickable - the pick path drops collapsed items - so the
  // filter's elements are the only ones you can select.
  // OFF: nothing is hidden, so every element stays visible and selectable and
  // the filter only narrows the table and its numbers.
  //
  // It is a MODE, not a selection: a filter change does not reset it, it does
  // not highlight anything, and it is not a write shape.
  const [filteredOnly, setFilteredOnly] = useState(true);
  // WHAT THE APPLIED FILTER MATCHED, LIT IN THE MODEL (owner, 2026-09-21:
  // "after a filter is applied and or a gate rule is applied ... the 3D
  // model to update the blueish selection in real time and frame
  // everything"). Resolved by the server with the query a select-all
  // write runs, so what is lit is what would be written.
  const [filterUids, setFilterUids] = useState([]);
  // Bumped by every status write: a status filter's answer moves when a
  // status is written, and the quantities table must read its rows again.
  const [writeSeq, setWriteSeq] = useState(0);
  const [treeSeq, setTreeSeq] = useState(0);
  const statusWritten = useCallback(({ tree = true } = {}) => {
    setWriteSeq((n) => n + 1);
    if (tree) setTreeSeq((n) => n + 1);
    if (onStatusWritten) { try { onStatusWritten(); } catch (e) { /* the board still refreshed */ } }
  }, [onStatusWritten]);
  const [levelPick, setLevelPick] = useState([]);

  // SELECTING A CUSTOM CATEGORY'S PARTS, from either place that offers it -
  // the custom category list and the row in the model tree. The page's own
  // selection is what every downstream control reads, so there is no
  // second notion of "what is picked".
  const onSelectParts = useCallback((uids, name, definitionId) => {
    setSelected(uids);
    setPartsFrom(definitionId || "");
    setLevelPick([]);
    setRecordChecked([]);
    setPartsOpen(false);
    message.success(`${uids.length.toLocaleString()} parts selected: ${name}`);
  }, []);
  const [levelUids, setLevelUids] = useState([]);
  const [levelBusy, setLevelBusy] = useState(false);
  const [levelNote, setLevelNote] = useState("");
  const [onlySelectedBusy, setOnlySelectedBusy] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  // HOW MANY THE TICKS STAND FOR, which is NOT the length of any id list.
  // A category too big to send as ids is refused WITH ITS COUNT, and the
  // bar must say that count: it used to read "working out…" for ever while
  // Isolate went on to show a million elements, which is the two halves of
  // the page disagreeing about the same tick (leader, 2026-09-20).
  const [tickCount, setTickCount] = useState(null);   // { n, atLeast } | null
  const [autoFrame, setAutoFrame] = usePreference("tracking.autoFrame", true);

  // What the viewer is being asked to show: the ticks of EITHER tree (a
  // tick is a tick, whichever tree it was made in). NOT what is picked in
  // the model - THE CHECKBOXES DEFINE THE SET, a click is ordinary
  // selection (owner, 2026-09-20). The isolate boundary is the set, and a
  // highlight inside it never moves that boundary.
  const allChecked = useMemo(
    () => [...minimalKeys(checked), ...minimalRecordKeys(recordChecked)],
    [checked, recordChecked]
  );
  const selectionKeys = useMemo(() => sourceKeys(allChecked), [allChecked]);
  // WHICH CHECKBOXES DECIDE WHAT IS DRAWN: the MODEL ELEMENTS ticks, and
  // only those (owner, 2026-09-20: "selected elements from the model
  // source, the right checkboxes"). Tracking quantities ticks and viewer
  // clicks are a HIGHLIGHT INSIDE that set - they colour elements, they do
  // not decide which ones exist. So the isolate follows this list and the
  // selection colour follows selectionKeys above, which is both trees.
  const drawKeys = useMemo(() => minimalKeys(checked), [checked]);
  const drawStamp = drawKeys.join("~~");
  // RESOLVED ONLY WHEN THE ISOLATE IS ON AND ACTUALLY NARROWING. With
  // nothing ticked the whole model is drawn and the parts are visible
  // anyway, so there is nothing to add and no request worth making.
  useEffect(() => {
    let alive = true;
    if (!projectId || !onlySelected || !drawKeys.length) {
      setDerivedPartUids(null); setDerivedPartNote("");
      return undefined;
    }
    (async () => {
      try {
        const list = await getDerivedCategories(projectId, {});
        if (!alive) return;
        const all = (list && list.categories) || [];
        const wanted = derivedIds && derivedIds.length
          ? all.filter((d) => derivedIds.includes(String(d._id)))
          : all;
        if (!wanted.length) { setDerivedPartUids([]); setDerivedPartNote(""); return; }
        const out = [];
        const refused = [];
        for (const d of wanted) {
          // eslint-disable-next-line no-await-in-loop
          const r = await getDerivedCategoryUids(projectId, String(d._id), { documentIds: prefixFilter || undefined })
            .catch(() => null);
          if (!alive) return;
          // OVER THE RESOLVER'S LIMIT THE WHOLE LIST IS REFUSED, never a
          // partial one - and a category whose parts cannot be resolved
          // stays hidden, so it is NAMED rather than quietly missing.
          if (!r || r.tooMany) { refused.push(d.name || "a custom category"); continue; }
          out.push(...expandCompact(r || {}));
        }
        if (!alive) return;
        setDerivedPartUids([...new Set(out)]);
        setDerivedPartNote(refused.length
          ? `${refused.join(", ")} ${refused.length === 1 ? "has" : "have"} too many parts to show while "Tracked elements" is on, so ${refused.length === 1 ? "its" : "their"} parts stay hidden.`
          : "");
      } catch (e) {
        if (alive) { setDerivedPartUids([]); setDerivedPartNote(""); }
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, onlySelected, drawStamp, derivedCsv, prefixFilter]);

  // THE DRAWN SET, PLUS THOSE PARTS. A new rule rather than a mutated one:
  // the rule object is handed to the viewer and compared by reference, and
  // mutating the set in place would not re-draw.
  const drawRuleWithParts = useMemo(() => {
    if (!drawRule) return drawRule;
    const add = [...new Set([...(derivedPartUids || []), ...tickedPartUids])];
    if (!add.length) return drawRule;
    return new FragmentIsolation({
      hashes: drawRule.hashes,
      // The parts join the single-element uids, which is the set `has()`
      // tests per element. Their 36-character document prefix is their
      // parent's, so the prefix scope test passes wherever the parent's
      // file is on.
      singles: new Set([...drawRule.singles, ...add]),
      prefixes: drawRule.prefixes || [],
      excludeUids: drawRule.excludeUids,
      // What it STANDS FOR grows by the parts it now draws, so the budget
      // decision upstream sees the real size.
      elementCount: (drawRule.elementCount || 0) + add.length,
    });
  }, [drawRule, derivedPartUids, tickedPartUids]);
  // And what is ticked INSIDE that set: the Tracking quantities rows.
  // These are the only ticks that should PAINT once the set is isolated -
  // painting the set itself green says nothing, because the set is
  // already the only thing drawn (leader, 2026-09-20: "if the set keeps
  // arriving as highlightRule then every element in the set paints green
  // and the highlight stops meaning anything").
  const innerKeys = useMemo(() => minimalRecordKeys(recordChecked), [recordChecked]);
  const innerStamp = innerKeys.join("~~");
  // THE CUSTOM CATEGORIES TICKED IN THAT TABLE, resolved to their parts.
  //
  // This exists because `selectionUids.parseKey` does not know a
  // `derived:` key and never will: the node-key vocabulary is the model
  // tree's, and a definition is not a part of the model tree
  // (utils/trackingElements refuses one out loud for the same reason). So
  // the rule resolver DROPS these keys - deliberately, which is only
  // acceptable because this lane picks them up. Without it a tick on a
  // custom category resolved to a rule standing for nothing, the bar read
  // 0, and the model lit nothing: the shape of every silent-drop bug on
  // this page.
  const innerDerivedIds = useMemo(() => nodesForKeys(recordChecked).derivedIds, [recordChecked]);
  // A RESTORED TICK THIS PAGE NO LONGER READS is skipped and said in words,
  // never thrown and never dropped in silence (leader, 2026-10-17).
  const skippedTickWords = useMemo(() => nodesForKeys(recordChecked).skipped.map((x) => x.reason).join("; "), [recordChecked]);
  useEffect(() => { if (skippedTickWords) message.warning(`Some saved ticks were skipped: ${skippedTickWords}`, 8); }, [skippedTickWords]);
  const innerDerivedCsv = innerDerivedIds.join(",");
  useEffect(() => {
    let alive = true;
    if (!projectId || !innerDerivedIds.length) { setTickedPartUids([]); setTickedPartNote(""); return undefined; }
    (async () => {
      const out = [];
      const refused = [];
      for (const id of innerDerivedIds) {
        // eslint-disable-next-line no-await-in-loop
        const r = await getDerivedCategoryUids(projectId, id, { documentIds: prefixFilter || undefined })
          .catch(() => null);
        if (!alive) return;
        // OVER THE RESOLVER'S LIMIT, OR UNRESOLVABLE: named, never a
        // silent partial light. "30,240 parts is more than can be lit at
        // once" is actionable; a tick that quietly paints nothing is the
        // complaint that started all of this.
        if (!r || r.tooMany) { refused.push(r && r.count ? Number(r.count) : 0); continue; }
        out.push(...expandCompact(r || {}));
      }
      if (!alive) return;
      setTickedPartUids([...new Set(out)]);
      setTickedPartNote(refused.length
        ? `${refused.length === 1 ? "A ticked custom category has" : `${refused.length} ticked custom categories have`} too many parts to light in the model at once${refused[0] ? ` (${refused[0].toLocaleString()})` : ""} - the count, the table and the status still cover all of them.`
        : "");
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, innerDerivedCsv, prefixFilter]);
  // THE ACTIVE SET NAMES THE SELECTION TAB (owner, 2026-09-20). A set is a
  // tracking list; when one is chosen the board is already filtered to it,
  // so the tab that shows what is selected carries ITS name rather than
  // the word "Selection", with its element count underneath. The label
  // itself is rendered in ModelStage (root-f5's file) from `detailsLabel`.
  const activeSet = useMemo(
    () => lists.find((l) => String(l._id) === String(activeList)) || null,
    [lists, activeList]
  );
  // An exclusion only means something while something above it is ticked.
  useEffect(() => {
    setExcluded((prev) => {
      const next = pruneExclusions(prev, [...checked, ...recordChecked]);
      return next.length === prev.length ? prev : next;
    });
  }, [checked, recordChecked]);
  // THE THREE RULES, in the one place where getting them wrong is
  // invisible. It took an evening and four people to arrive at three
  // lines, so they are written here rather than remembered:
  //
  //   Model elements ticks                          -> WHAT IS DRAWN
  //   the selection (quantities + levels + clicks)  -> WHAT IS LIT,
  //                                                    ACTED ON, PRINTED
  //   a plain click REPLACES; ctrl-click ADDS
  //
  // The guard in the third line is what makes the second legal. The owner
  // ruled at 20:35 that an inspection click must never join the printed
  // schedule - and his objection was to ACCUMULATION, not to clicks being
  // actionable. Replace-on-plain-click removes the accumulation and keeps
  // the capability: a stray click can no longer swell what gets written,
  // because it replaces the selection instead of joining it.
  //
  // THE MODEL ELEMENTS TICKS ARE NOT IN HERE. They are the set: they
  // decide what is drawn and nothing else. Their own tab keeps its own
  // bar, which acts on exactly those ticks and says so.
  // The selection BEFORE a level narrows it. The level resolve asks about
  // this one - which is also what stops the two from chasing each other:
  // levels are worked out FROM the selection, so they cannot also be IN
  // the thing they are worked out from.
  //
  // AND WITH NOTHING SELECTED IT FALLS BACK TO THE SET. The panel
  // computes "elements per level" of SOMETHING, and the owner's own flow
  // is to tick a category in Model elements and then pick a level out of
  // it. Making the panel describe nothing until a quantities row is
  // ticked would break that flow in the name of a rule about what gets
  // PRINTED. So: the selection when there is one, the set when there is
  // not - which is also what this page has always done, and what the bulk
  // bar on the Model elements tab acts on.
  const baseSelectionRaw = useMemo(() => {
    // SELECT-ALL-FILTERED IS THE SELECTION WHEN IT IS ON. The filter
    // travels rather than a list of ids: the server re-resolves it at
    // count time and again at write time, so what is written is what the
    // filter matched and not what happened to be rendered when the box
    // was ticked. A million-element match costs the same to carry as a
    // twelve-element one.
    const rec = nodesForKeys(recordChecked);
    const uids = [...new Set([...(selected || []), ...rec.uniqueIds])];
    const out = {};
    if (rec.recordNodes.length) out.recordNodes = rec.recordNodes;
    if (uids.length) out.uniqueIds = uids;
    // A TICKED CUSTOM CATEGORY TRAVELS AS ITS DEFINITION, never as the
    // part ids it resolves to today (owner, via the leader: the three
    // categories "need to behave exactly like normal categories, when it
    // comes to operations and printing, and status"). The server
    // re-resolves it at count time and again at write time, so a
    // definition matching 47,000 clamps costs what one matching twelve
    // costs, and what is written is what the definition means after the
    // next publish rather than what it meant when the box was ticked.
    if (rec.derivedIds.length) out.derivedIds = rec.derivedIds;
    const ex = exclusionKeys(excluded);
    if (Object.keys(out).length) {
      if (ex.length) out.exclude = ex;
      // ROWS TICKED IN A FILTERED (OR SET-LIMITED) TABLE mean those rows AS
      // SHOWN: the filter travels with them and the server narrows the
      // count and the write by it. Without it a ticked "Walls" row read 4
      // in a set of 4 walls and wrote to every wall in the model.
      if (quantityFilters) out.filters = quantityFilters;
      return out;
    }
    // SHOW ONLY FILTERED stands in as the selection ONLY WHEN NOTHING IS
    // PICKED (owner, 2026-09-28: "why is selected showing 41,419 which is all
    // elements in level1, instead of following my mouse clicks and ctrl clicks
    // like a normal 3D view?").
    //
    // THIS BRANCH USED TO COME FIRST AND RETURN, so `selected` was never read
    // at all - his clicks were not outranked, they were DISCARDED. That is also
    // why elements "would not deselect": ctrl-click shrank the set correctly and
    // the panel was looking at a filter that could not show it. One bug.
    // The filter still travels rather than a list of ids, so a million-element
    // match costs what a twelve-element one costs - that part was right.
    if (allFiltered && quantityFilters) {
      const exf = exclusionKeys(excluded);
      return { filters: quantityFilters, ...(exf.length ? { exclude: exf } : {}) };
    }
    const keys = minimalKeys(checked);
    if (!keys.length) return null;
    return { nodeKeys: keys, ...(ex.length ? { exclude: ex } : {}) };
  }, [recordChecked, selected, excluded, checked, allFiltered, quantityFilters]);
  // Every selection shape carries the page's levels: the count, the level
  // pick and the write all stay on them (the server applies them once).
  const baseSelection = useMemo(
    () => (baseSelectionRaw && (cutLevels.length || cutUidsId)
      ? { ...baseSelectionRaw, ...(cutLevels.length ? { pageLevels: cutLevels } : {}), ...(cutUidsId ? { cutUids: cutUidsId } : {}) }
      : baseSelectionRaw),
    [baseSelectionRaw, cutLevelsKey, cutUidsId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // A LEVEL PICK NARROWS THE SELECTION, it does not widen it: "pick Level
  // 2, press Installed" marks Level 2 and not the category it came from.
  // So once a level is picked and resolved, the level's elements ARE the
  // selection. Until they arrive the base selection stands, so the count
  // on screen never blinks through zero on its way to the right number.
  const serverSelection = useMemo(() => {
    if (!levelPick.length) return baseSelection;
    if (!levelUids.length) return baseSelection;
    const out = { uniqueIds: levelUids };
    const ex = exclusionKeys(excluded);
    if (ex.length) out.exclude = ex;
    return out;
  }, [baseSelection, levelPick, levelUids, excluded]);

  // What the selection IS, in words, for the confirmation that acts on it.
  // "Set 1,240 elements to Installed?" does not say WHICH, and a precise
  // action deserves a precise sentence (leader, 2026-09-20).
  const selectionWords = useMemo(() => {
    const bits = [];
    if (allFiltered && quantityFilters) {
      const f = quantityFilters;
      const parts = [];
      if (f.name) parts.push(`named like "${f.name}"`);
      if ((f.levels || []).length) parts.push(`on ${f.levels.join(", ")}`);
      if ((f.statuses || []).length) parts.push(`${f.statuses.join(", ")}`);
      const ranges = Object.keys(f.ranges || {});
      if (ranges.length) parts.push(`by ${ranges.join(", ")}`);
      // The rules are named in the confirmation too: a person about to
      // write a status onto a set they cannot see in full should read
      // what defined it, not only how big it is.
      if ((f.rules || []).length) parts.push(rulesInWords(f));
      return `matching the filter${parts.length ? ` (${parts.join("; ")})` : ""}`;
    }
    if (levelPick.length) bits.push(`on ${levelPick.join(", ")}`);
    const rec = recordChecked.filter((k) => /^r[cgs]:/.test(String(k)));
    if (rec.length === 1) bits.push(`in ${recordKeyWords(rec[0])}`);
    else if (rec.length > 1) bits.push(`in ${rec.length} rows of Tracking quantities`);
    if ((selected || []).length === 1 && !rec.length && !levelPick.length) bits.push("you clicked in the model");
    else if ((selected || []).length > 1) bits.push(`including ${selected.length} picked in the model`);
    return bits.join(" ");
    // allFiltered and quantityFilters ARE inputs: without them this memo
    // kept the words it had before the select-all, so the confirmation
    // read "Set 4 elements to Installed?" instead of naming the rule that
    // chose them. The linter said so - as a WARNING, among other warnings,
    // and it was read as noise. Found by reading the confirmation in a real
    // browser rather than by any test.
  }, [levelPick, recordChecked, selected, allFiltered, quantityFilters]);

  // WHAT THE BULK BAR IS ABOUT TO ACT ON, said on the bar. Three states,
  // and the middle one is the sharp edge the leader named: with nothing
  // selected the selection falls back to the SET, and a set can be a
  // whole category - 1,049,917 elements on SpaceX. A bar that reads the
  // same in both states is one a person presses believing it means the
  // twelve things they were looking at.
  // WHAT THE FILTER HIDES IN 3D (owner, 2026-09-28: "filtering the tracked
  // quantities, show hide elements from 3D view as a whole").
  //
  // SAFE PRECISELY BECAUSE filterUids IS COMPLETE OR EMPTY. The server answers
  // `tooMany` for a match too large to enumerate, and the effect above then
  // sets filterUids to [] AND tells the person in words. So a non-empty set is
  // the WHOLE match, never a cap - and a complement built from a capped set
  // would hide elements that actually match, which is the one way this feature
  // could lie. Empty means no rule, and the model is left exactly as it was.
  // OFF means show everything: no rule, so every element is drawn and every
  // element can be picked. See hideOutside for what ON now covers.
  const filterHiddenRule = useMemo(
    () => hideOutside({ filteredOnly, innerKeys, innerRule, quantityFilters, filterUids, quantityTouched }),
    [quantityFilters, filterUids, filteredOnly, innerKeys, innerRule, quantityTouched]
  );
  const hideNote = useMemo(
    () => hideNoteFor({ filteredOnly, innerKeys, innerRule, quantityTouched, quantityFilters }),
    [filteredOnly, innerKeys, innerRule, quantityTouched, quantityFilters]
  );

  // Kept for the confirmation wording below, which still has to say when the
  // TABLE's select-all is what a press would act on.
  const showOnlyFiltered = !!(allFiltered && quantityFilters);
  const bulkActsOn = useMemo(() => {
    if (!serverSelection) return "";
    // WHICH SET, IN WORDS, BEFORE THE CLICK (leader, 2026-09-28). A count alone
    // is not a statement of scope - the number was always right and what it was
    // counting was never said, which is how 41,419 passed for a selection for
    // four days. The filtered case must be unmistakably different from the
    // selection case, so it names the filter rather than saying "selection".
    if (serverSelection.filters && !serverSelection.uniqueIds && !serverSelection.recordNodes) {
      return "every element matching the quantity tracking filter - not what you picked in the model";
    }
    if (serverSelection.nodeKeys) return "everything ticked in Model elements";
    if (serverSelection.uniqueIds) return selectionWords || "the elements you picked in the model";
    return selectionWords || "your selection";
  }, [serverSelection, selectionWords]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pick something else and the reason for the pause is gone: untick the
  // category that was too big and "Tracked elements" comes back on,
  // without anyone having to find the switch again.
  const selectionStamp = selectionKeys.join("~~");
  useEffect(() => { setIsolatePaused(false); }, [selectionStamp]);

  // ONE summary per settled selection, serving BOTH the bulk bar's count
  // and the Selection details panel - so "one number in every place" is
  // not a discipline, it is the same number. The server subtracts the
  // exclusions; nothing here re-derives it.
  const [selSummary, setSelSummary] = useState(null);
  const [quantitiesState, setQuantitiesState] = useState("");
  useEffect(() => {
    let alive = true;
    if (!projectId || !serverSelection) { setSelSummary(null); return undefined; }
    const t = setTimeout(() => {
      getSelectionSummary(projectId, { ...serverSelection, documentIds: prefixFilter })
        // FINISHED-WITH-NOTHING IS `undefined`, NOT `null`, and the
        // difference is a PERMANENT SPINNER. SelectionDetails reads this prop
        // as three states: undefined = nobody is supplying one, the panel owns
        // the fetch; null = the page owns it and has NOT ANSWERED YET, wait;
        // an object = the answer. This page was answering `null` for a
        // SUCCESSFUL-but-empty response and for a FAILED request too - both
        // already finished - so the panel sat on setBusy(true) for ever where
        // it used to fetch and recover. Demonstrated on staging: one forced
        // 504 left 1 spinner and no data. Saying `undefined` hands the fetch
        // back, which is what undefined already means.
        //
        // AND THE ONE THING THIS SPELLING DEPENDS ON (Mark, 2026-09-25).
        // `undefined` here means BOTH "finished with nothing" and "panel, go
        // and ask" - two different instructions with one spelling. That is
        // safe today for a reason OUTSIDE this file: the route has exactly one
        // empty-success path and it is free. POST /selection/summary returns
        // 200 { summary: null } from an EARLY RETURN when there is no
        // published model, before any query; a selection that resolves to
        // nothing is a 400, not an empty 200. So the extra request this causes
        // is instant. MEASURED on staging: the empty case makes 2 requests
        // where the happy path makes 1, with no spinner either way.
        // IF ANYONE EVER MAKES summarise RETURN AN EMPTY SUMMARY ON A REAL
        // QUERY instead of a 400 - a one-line change in that route, and a
        // reasonable-looking one - this line starts re-asking a question whose
        // worst prod call was 45 SECONDS, and nobody will connect it to here.
        // At that point the page needs a fourth state (failed, separate from
        // finished-with-nothing) rather than this spelling.
        .then((r) => { if (alive) { setSelSummary(r.summary || undefined); setQuantitiesState(r.quantities || ""); } })
        .catch(() => { if (alive) setSelSummary(undefined); });
    }, 120);
    return () => { alive = false; clearTimeout(t); };
    // writeSeq: a status write moves the selection's per-status counts.
  }, [projectId, JSON.stringify(serverSelection), prefixFilter, writeSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  // What the bars say. The summary is the better number - it subtracts the
  // exclusions - but ONLY where the quantities exist: on a project with no
  // quantity build it would read 0 while the isolate shows a million, which
  // is the two-numbers problem wearing a different hat.
  // WHAT IS PAINTED, PLUS THE TICKED PARTS. Built the same way as the
  // drawn rule above and for the same reason: a new object, because the
  // viewer compares rules by reference.
  //
  // asFragmentRule, not a bare object: the store calls `.has()` and a
  // plain `{singles}` silently matched nothing (feedback: normalise where
  // .has is CALLED).
  const innerRuleWithParts = useMemo(() => {
    if (!tickedPartUids.length) return innerRule;
    if (!innerRule) {
      // A CUSTOM CATEGORY ON ITS OWN still paints: this is the case the
      // rule resolver cannot answer at all, so without this branch the
      // one tick he is most likely to make in isolation lights nothing.
      return asFragmentRule({ hashes: [], singles: tickedPartUids, elementCount: tickedPartUids.length });
    }
    return new FragmentIsolation({
      hashes: innerRule.hashes,
      singles: new Set([...innerRule.singles, ...tickedPartUids]),
      prefixes: innerRule.prefixes || [],
      excludeUids: innerRule.excludeUids,
      elementCount: (innerRule.elementCount || 0) + tickedPartUids.length,
    });
  }, [innerRule, tickedPartUids]);

  const barCount = useMemo(() => {
    if (quantitiesState === "done" && selSummary) return { n: selSummary.elements, atLeast: false };
    return tickCount;
  }, [quantitiesState, selSummary, tickCount]);
  // HOW MANY PARTS THE SELECTION STANDS FOR, said in the word "parts" and
  // never added to the element count: 47,000 clamps inside 6,900 hangers
  // is two numbers about two populations, and a single total would be a
  // number nobody can point at in the model (the pre-decision agreed with
  // the leader before this was built).
  const barParts = useMemo(
    () => ((selSummary && selSummary.parts && selSummary.parts.count) || 0),
    [selSummary]
  );
  // The one line the bars show instead of the plain element count when
  // parts are in play. With no parts this is empty and the bar reads
  // exactly as it always has.
  const barWords = useMemo(() => {
    if (!barParts) return "";
    const parts = `${barParts.toLocaleString()} part${barParts === 1 ? "" : "s"}`;
    const n = barCount ? barCount.n : null;
    if (!n) return parts;
    return `${n.toLocaleString()} element${n === 1 ? "" : "s"} · ${parts}`;
  }, [barParts, barCount]);
  // COMPUTED HERE, NOT IN THE ATTRIBUTE. A multi-line `||` inside the
  // countNote prop makes eslint-plugin-react-hooks mis-read this file and
  // report 34 phantom "hook called conditionally" errors - the same quirk
  // that cost an hour on 2026-09-28, bisected again here. One identifier
  // in the JSX, the logic in a line of its own.
  const tooManyNote = barCount && barCount.atLeast ? `more than ${barCount.n.toLocaleString()} elements` : "";
  const barCountNote = barWords || tooManyNote;

  // ONE REQUEST PER SETTLED SELECTION - and until 2026-09-20 there were
  // TWO, which is the bug behind the owner's "the tick does not
  // highlight". This effect used to resolve the ticks a SECOND time, as
  // strict ids, purely to paint them and to count them; the rule below
  // answers both (its `singles` carries the ids of any row answered as
  // ids, and its elementCount is the count). Two questions about one tick,
  // racing each other, is what produced "hadRule false, listUids 0" about
  // two runs in three: whichever landed second could find the other had
  // already moved the page on.
  //
  // What remains is a plain click in the model, which is not a tick and
  // needs no server at all.

  // What the viewer highlights as a LIST: only what was clicked in the
  // model. Everything ticked in a tree travels as the rule, which the
  // store ORs with this - so a million-element category costs no ids at
  // all and a click still works on its own.
  // What the viewer highlights as a LIST: what was clicked in the model,
  // and what a level pick resolved to. Both are highlights inside the
  // drawn set - neither is part of it.
  // THE FILTER IS LIT WHEN NOTHING MORE DELIBERATE IS: a tick in
  // Tracking quantities or a level pick is a person choosing inside the
  // filtered table, and that choice is what shows. With neither, the model
  // shows everything the filter matched - and moves the moment the filter
  // or a gate rule changes.
  // With ticks in the table, the lit set is those ticks WITHIN the filter
  // (and the set) - asked of the server below - not the whole category.
  // The page's levels alone are not a thing to light - choosing Level 2
  // must not paint Level 2 blue. They narrow what IS lit: a header filter,
  // a rule, the set, or ticks in the table.
  const qfOwn = !!quantityFilters && !!(quantityFilters.name || (quantityFilters.levels || []).length
    || (quantityFilters.statuses || []).length || Object.keys(quantityFilters.ranges || {}).length
    || (quantityFilters.rules || []).length || quantityFilters.listId);
  const filterLit = !!quantityFilters && !levelUids.length && (qfOwn || innerKeys.length > 0);
  const litByTicks = ticksAreLit(quantityFilters, levelUids, innerKeys);
  const selectionUids = useMemo(
    () => litUids(selected, levelUids, filterUids, litNow(litByTicks, quantityFilters, levelUids)),
    [selected, levelUids, litByTicks, filterUids, quantityFilters]
  );
  // THE RULE IS THE SELECTION, not the isolate (2026-09-20). It used to be
  // worked out only while "Tracked elements" was on, so with the switch
  // off the viewer had NOTHING for a category answered as hashes - which
  // is why ticking a whole category selected it but highlighted nothing
  // (owner). It is now resolved for every settled selection and handed to
  // the viewer as highlightRule. ISOLATION IS A DIFFERENT QUESTION: it
  // follows drawKeys - the Model elements ticks - so ticking a row in
  // Tracking quantities colours elements without changing which ones are
  // drawn (owner, 2026-09-20).
  // THE LATEST ASK WINS, AND A SUPERSEDED ANSWER IS DROPPED KNOWINGLY.
  //
  // This used to guard with a per-run `alive` flag and fire the moment the
  // keys changed, while a SECOND effect resolved the same tick as ids on a
  // 120 ms debounce. Two questions, two requests, no shared order - and
  // the page kept neither answer about two runs in three (owner: "the tick
  // does not highlight"; the server was answering 13 uids both times).
  //
  // Now: one question, on the same settle as everything else, with a
  // SEQUENCE - a response may only write if it is still the newest ask.
  // `alive` could only say "this run was torn down"; a sequence says
  // "something newer is already on its way", which is the thing that
  // actually decides who should win.
  const askSeq = useRef(0);
  // `onlySelected` IS NOT AN INPUT TO THE QUESTION (leader, 2026-09-20,
  // from reading the body rather than the deps): inside the resolve it
  // only chooses whether a message is shown and whether the isolate is
  // paused. Whether the rule is APPLIED as isolation is decided
  // downstream. Leaving it in the deps meant the resolve re-fired on a
  // value that changes BY ITSELF after mount - helpers/userPrefs seeds it
  // three ways, including a GET that lands at an arbitrary moment - so a
  // network response could restart a question whose answer was already on
  // its way. It reads through a ref instead.
  const onlySelectedRef = useRef(onlySelected);
  useEffect(() => { onlySelectedRef.current = onlySelected; }, [onlySelected]);
  // The same disease, one step further: both of these are ARRAYS compared
  // by reference, so anything that re-sets them to equal content re-fires
  // the resolve. The question is what they SAY, so the deps are what they
  // say.
  const excludedStamp = exclusionKeys(excluded).join("~~");
  useEffect(() => {
    if (!projectId) {
      setOnlySelectedUids(null); setSelectionRule(null); setDrawRule(null); setInnerRule(null); setOnlySelectedBusy(false);
      trace('resolve:skip', { why: 'no project' });
      return undefined;
    }
    // NOTHING ticked shows the WHOLE model. With the switch on by default,
    // isolating "nothing" would open the tab on an empty viewer, which reads
    // as a broken page rather than as an empty selection.
    if (!selectionKeys.length) {
      setOnlySelectedUids(null); setSelectionRule(null); setDrawRule(null); setInnerRule(null); setOnlySelectedBusy(false); setTickCount(null);
      trace('resolve:none', { why: 'nothing selected' });
      return undefined;
    }
    const mine = (askSeq.current += 1);
    const keys = selectionKeys.slice();
    setOnlySelectedBusy(true);
    trace('resolve:start', { seq: mine, keys: keys.length, first: keys[0] || '' });
    const t = setTimeout(() => {
      // The wall time from ask to answer, recorded beside the outcome: a
      // fast answer and a slow one are different situations, and until
      // they are on the same line nobody can tell which one loses
      // (leader's prediction, 2026-09-20 - falsifiable in one run).
      const asked = Date.now();
      ruleForSelection(projectId, keys, { prefixes: prefixFilter, exclude: exclusionKeys(excluded) })
        .then((r) => {
          if (askSeq.current !== mine) { trace('resolve:superseded', { seq: mine, latest: askSeq.current, ms: Date.now() - asked }); return; }
          if (r.tooMany) {
            const k = r.tooMany.key;
            const what = k.typeName || k.family || k.group || k.category;
            // A refusal is a REASON, not a silence: it used to speak only
            // when "Tracked elements" was on, so with the switch off the
            // page simply showed nothing and said nothing.
            const say = onlySelectedRef.current;
            trace('resolve:refused', { seq: mine, ms: Date.now() - asked, count: r.tooMany.count, what, total: !!r.tooMany.total, said: !!say });
            if (say) message.warning(r.tooMany.total
              ? `That selection is ${r.tooMany.count.toLocaleString()} elements together - more than can be picked out at once. "Tracked elements" has gone off and the whole model is shown; untick some rows, starting with "${what}".`
              : `"${what}" is ${r.tooMany.count.toLocaleString()} elements - more than can be picked out at once. "Tracked elements" has gone off and the whole model is shown; tick a level below it instead.`);
            if (say) pauseOnlySelected();
            setOnlySelectedUids(null);
            setSelectionRule(null);
            setDrawRule(null);
            setInnerRule(null);
            // The count is still known, and the bar still says it.
            setTickCount({ n: r.tooMany.count, atLeast: !!r.tooMany.total });
            return;
          }
          setSelectionRule(r.rule);
          setOnlySelectedUids(null);
          // TWO NARROWER QUESTIONS, ASKED ONLY WHEN THEY ARE DIFFERENT
          // QUESTIONS. What is DRAWN comes from the Model elements ticks,
          // what is PAINTED from the Tracking quantities ticks; each one
          // is either empty (nothing to ask), the whole selection (the
          // answer already in hand), or a subset - and only a subset
          // costs a request. Tick in one tree only, which is what almost
          // everyone does, and this is still one request in total.
          const narrower = (k, set) => {
            if (!k.length) { set(null); return; }
            if (k.length === keys.length) { set(r.rule); return; }
            ruleForSelection(projectId, k.slice(), { prefixes: prefixFilter, exclude: exclusionKeys(excluded) })
              .then((d) => {
                if (askSeq.current !== mine) return;
                // A refusal isolates NOTHING rather than isolating half:
                // a partial isolate reads as a filtered model.
                set(d.tooMany ? null : d.rule);
              })
              .catch(() => { if (askSeq.current === mine) set(null); });
          };
          narrower(drawKeys, setDrawRule);
          narrower(innerKeys, setInnerRule);
          // ONE number from ONE place: what the rule stands for is what the
          // bar counts, so there is nothing left to disagree with it.
          setTickCount({ n: r.elementCount, atLeast: false });
          trace('resolve:ok', {
            seq: mine, ms: Date.now() - asked, elements: r.elementCount,
            hashes: (r.rule && r.rule.hashes) ? r.rule.hashes.length : 0,
            singles: (r.rule && r.rule.singles) ? r.rule.singles.length : 0,
            excluded: (r.rule && r.rule.excludeUids) ? r.rule.excludeUids.length : 0,
          });
          // The words the leader approved (handoffs/root-8e-isolate-messages.md).
          // A row answered as ids says nothing at all; only a big one is worth
          // a sentence, and then it explains the thing a person would
          // otherwise read as a bug.
          if (onlySelectedRef.current && r.anyHashes && r.elementCount > 0) {
            const what = keys.length === 1 ? (parseKeyLabel(keys[0]) || "your selection") : "your selection";
            const shared = prefixFilter ? "These parts are shared with other model files, so every copy is shown. " : "";
            message.info(`${shared}Showing only ${what} - ${r.elementCount.toLocaleString()} elements. Distant parts are shown in less detail until you move closer.`);
          }
        })
        .catch((e) => {
          if (askSeq.current !== mine) { trace('resolve:superseded', { seq: mine, latest: askSeq.current, failed: true }); return; }
          // A catch that nulls without a word is how a day disappears.
          trace('resolve:error', { seq: mine, ms: Date.now() - asked, message: (e && e.message) || String(e) });
          if (onlySelectedRef.current) { message.error(e.message || "Could not work out the selection"); pauseOnlySelected(); }
          setOnlySelectedUids(null); setSelectionRule(null); setDrawRule(null); setInnerRule(null); setTickCount(null);
        })
        .finally(() => { if (askSeq.current === mine) setOnlySelectedBusy(false); });
    }, 120);
    // The timer is cleared, but the SEQUENCE is not rewound: a request
    // already in flight stays superseded rather than becoming current
    // again if this effect re-runs with the same keys.
    return () => { clearTimeout(t); };
    // WHAT THE QUESTION IS, and nothing else: the project, what is
    // selected, which files, and what was dropped. Not who is listening,
    // not a preference that arrives from the network after mount.
  }, [projectId, selectionStamp, drawStamp, innerStamp, prefixFilter, excludedStamp]); // eslint-disable-line react-hooks/exhaustive-deps

  // THE LEVEL PICK, RESOLVED. The same sequence discipline as the
  // selection resolve above: the latest ask wins and a superseded answer
  // is dropped knowingly, because two levels ticked quickly is exactly
  // the case where the slower answer used to land last and win.
  const levelSeq = useRef(0);
  useEffect(() => {
    const mine = (levelSeq.current += 1);
    if (!projectId || !baseSelection || !levelPick.length) {
      setLevelUids([]); setLevelBusy(false); setLevelNote("");
      return undefined;
    }
    setLevelBusy(true);
    const t = setTimeout(() => {
      getSelectionLevelUids(projectId, { ...baseSelection, levels: levelPick, documentIds: prefixFilter })
        .then((r) => {
          if (levelSeq.current !== mine) return;
          if (r.tooMany) {
            // The same refusal the trees give, in the same words: a count
            // and what to do, never a silent empty highlight.
            setLevelUids([]);
            setLevelNote(`${(r.tooMany.count || 0).toLocaleString()} elements on those levels - too many to pick out at once; choose one level, or narrow the selection first.`);
            return;
          }
          const ids = [];
          Object.entries(r.byPrefix || {}).forEach(([p, list]) => {
            String(list).split(",").filter(Boolean).forEach((x) => ids.push(`${p}-${x}`));
          });
          (r.other || []).forEach((u) => ids.push(String(u)));
          setLevelUids(ids);
          setLevelNote(`${ids.length.toLocaleString()} element${ids.length === 1 ? "" : "s"} on ${levelPick.length} level${levelPick.length === 1 ? "" : "s"} - highlighted in the model`);
        })
        .catch((e) => {
          if (levelSeq.current !== mine) return;
          setLevelUids([]); setLevelNote(e.message || "Could not work out that level");
        })
        .finally(() => { if (levelSeq.current === mine) setLevelBusy(false); });
    }, 120);
    return () => clearTimeout(t);
  }, [projectId, JSON.stringify(serverSelection), levelPick.join("~~"), prefixFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // The clicked elements as a Set, for the trees to test a row against.
  // Built once per change rather than per row: a category can hold a
  // million rows and `includes` on an array is how a tree becomes slow
  // without anybody noticing which line did it.
  const pickedSet = useMemo(() => new Set(selected || []), [selected]);

  // A SELECT-ALL BELONGS TO THE FILTER IT WAS MADE UNDER. Change the
  // filter and "everything that matched" means a different set, so the
  // box clears rather than silently standing for the new one.
  const quantityFiltersStamp = JSON.stringify(quantityFilters);
  useEffect(() => { setAllFiltered(false); }, [quantityFiltersStamp]);

  // THE FILTER, RESOLVED FOR THE MODEL. The latest ask wins, as everywhere
  // on this page. Too many to light is SAID, with the count, and lights
  // nothing - never the first N, which would look like the whole match.
  const filterSeq = useRef(0);
  useEffect(() => {
    const mine = (filterSeq.current += 1);
    if (!projectId || !quantityFilters || !filterLit) { setFilterUids([]); return undefined; }
    const t = setTimeout(() => {
      const rec = innerKeys.length ? nodesForKeys(recordChecked) : null;
      getFilterUids(projectId, {
        filters: quantityFilters, documentIds: prefixFilter || undefined,
        ...(rec && rec.recordNodes.length ? { recordNodes: rec.recordNodes } : {}),
        ...(rec && rec.uniqueIds.length ? { uniqueIds: rec.uniqueIds } : {}),
      })
        .then((r) => {
          if (filterSeq.current !== mine) return;
          if (r.tooMany) {
            setFilterUids([]);
            message.info(`${Number(r.count || 0).toLocaleString()} elements match the filter - too many to highlight in the model at once. The table and a select-all still cover all of them; narrow the filter to see them lit.`);
            return;
          }
          const ids = [];
          Object.entries(r.byPrefix || {}).forEach(([p, list]) => {
            String(list).split(",").filter(Boolean).forEach((x) => ids.push(`${p}-${x}`));
          });
          (r.other || []).forEach((u) => ids.push(String(u)));
          setFilterUids(ids);
        })
        .catch((e) => {
          if (filterSeq.current !== mine) return;
          setFilterUids([]);
          message.error(e.message || "Could not highlight what the filter matched");
        });
    }, 120);
    return () => clearTimeout(t);
  }, [projectId, quantityFiltersStamp, prefixFilter, writeSeq, innerStamp, filterLit]); // eslint-disable-line react-hooks/exhaustive-deps

  // A pick belongs to the selection it was made in: change what is ticked
  // and the levels under it are different levels.
  useEffect(() => { setLevelPick([]); }, [selectionStamp]);

  // What was last clicked in the model, in full. Clicking used to answer
  // with eight characters of a GUID, which tells nobody what they are
  // looking at (owner, 2026-09-09).
  const [picked, setPicked] = useState(null);
  const [pickLoading, setPickLoading] = useState(false);
  const pickSeq = useRef(0);
  // Which multi-selection the person closed the floating panel on, so Close
  // still closes when there is no clicked element to drop (see panelNode).
  const [panelClosedFor, setPanelClosedFor] = useState("");

  // A box drawn in the model: the elements in it ARE the selection (lit in
  // the model, counted on the bar), and Tracking quantities narrows to them
  // so a select-all there marks exactly the box.
  const boxSeq = useRef(0);
  const onBoxSelect = useCallback(async (uids) => {
    const mine = (boxSeq.current += 1);
    if (!uids || !uids.length) {
      message.info("Nothing loaded inside that box - only elements drawn on screen can be box-selected");
      return;
    }
    setSelected(uids);
    // A BOX IS A FRESH PICK, SO IT SUPERSEDES THE LEVEL PICK (owner,
    // 2026-09-28: "why is level1 still selected after even i selected other
    // items in construction tracking?"). serverSelection returns the LEVEL's
    // elements whenever a level is picked and resolved, so a level left
    // standing after a new pick keeps reporting Level 1 however much he
    // selects. Both acts are deliberate; the LATER one wins.
    setLevelPick([]);
    try {
      const r = await createQuantitySelection(projectId, uids);
      if (boxSeq.current !== mine) return;
      setBoxSel({ id: r.selectionId, count: r.count });
      // THIS SENTENCE WAS TRUE UNTIL 2026-09-24 AND MY OWN CHANGE MADE IT
      // FALSE. Tracking quantities no longer narrows to a selection
      // (owner: it shows the rule-filtered elements only, always), so
      // promising that it does would have been the screen lying about
      // the change I had just made one file away.
      message.success(`${Number(r.count).toLocaleString()} element${r.count === 1 ? "" : "s"} selected - the Selection panel shows them; Tracking quantities still shows what the rules cover`);
    } catch (e) {
      if (boxSeq.current !== mine) return;
      setBoxSel(null);
      message.error(e.message || "Could not keep that selection");
    }
  }, [projectId]);

  // THE VIEWER'S LIVE SELECTION, during the drag and on release
  // (Paul's selectionChanged, 2026-09-24). The owner asked that what is
  // selected in 3D be reflected as it happens, so this runs per frame
  // while a rectangle is being drawn.
  //
  // TWO RULES THAT ARE NOT OPTIONAL, both of them his:
  //
  // 1. MID-DRAG, `uniqueIds` IS ABSENT - NOT AN EMPTY ARRAY. An absent
  //    key means "identity has not been sent yet"; an empty array on
  //    release means "the box caught nothing". Reading the first as the
  //    second would clear the panel at the start of every drag instead
  //    of filling it, which is the absence-read-as-a-value failure we
  //    have hit at three other layers today. So this branches on whether
  //    the KEY IS PRESENT, never on length.
  // 2. A LATE TICK MUST NOT OVERWRITE A NEWER ONE. The stream is per
  //    frame; the highest `seq` wins and anything older is dropped.
  // THE SAVED TRACKING SCOPE, resolved BEFORE the stage mounts.
  //
  // The viewer's row filter drops excluded instances where they are
  // BORN, so a tile that landed before the scope arrived would stay
  // built. Gating the stage on `ready` is what makes "never loaded"
  // true rather than "hidden afterwards".
  //
  // `ready` is never left false - a refusal, a timeout, a stale commit
  // or an unreachable route all resolve it and set `scopeNote`, so the
  // page draws the whole model and SAYS the scope could not be applied
  // rather than showing an empty stage.
  //
  // THE COMMIT IS NULL ON PURPOSE AND THE STALE CHECK IS THEREFORE
  // DORMANT: neither this page nor ModelStage knows which commit the
  // viewer is drawing, so there is nothing yet to compare the server's
  // answer against. The path exists and is tested; it starts working
  // the day the drawn commit is surfaced from the manifest. Passing
  // anything else here would be inventing an id.
  // THE SAVE AND THE APPLY ARE TWO ACTS (owner, 2026-09-25: keep the
  // autosave AND add the button, "for human behaviour"). The data save
  // is cheap and automatic; the view transition is expensive and
  // explicit, so the viewer rebuilds once rather than once per tick.
  //
  // `savedNonce` counts persisted scopes. `applyNonce` is the one the
  // person has asked to SEE. The hook reports which nonce the answer on
  // screen belongs to, so "saved but not applied" is a comparison
  // rather than a guess (utils scopeApplyState).
  const { ready: scopeReady, note: scopeNote, appliedNonce } = useTrackingScope(projectId, null, { nonce: applyNonce });
  // THE THREE STATES, COMPUTED OUTSIDE THIS COMPONENT (scopeApplyState
  // scopeApplyFrom). Every branch it makes is a branch not taken here:
  // this file's hooks linter counts code paths through the whole
  // component and reported eleven "called conditionally" errors at
  // unrelated hooks when the ternaries lived inline.
  //
  // appliedNonce is the generation THE LOADER HAS FILTERED UNDER, not
  // the page's belief - committing a scope is not applying it - and it
  // is null across a teardown, which must read as busy rather than as
  // applied.
  const scopeApply = scopeApplyFrom({ savedNonce, applyNonce, appliedNonce, stats });

  // WHAT A SAVE WOULD REMOVE, asked while it is still a decision. The
  // server counts it and names any custom category it would empty; the
  // page never works it out, so the warning cannot disagree with the
  // numbers the category itself shows.
  useEffect(() => {
    if (!projectId || scopeApply.key !== "pending") { setScopePreview(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      getScopePreview(projectId, {
        nodeKeys: scopeKeys,
        excludedNodeKeys: (scope && scope.excludedNodeKeys) || [],
        documentIds: prefixFilter,
      }).then((r) => { if (alive) setScopePreview(r); })
        .catch(() => { if (alive) setScopePreview(null); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, scopeApply.key, scopeKeysCsv, prefixFilter]);

  const selSeq = useRef(0);
  const [liveCount, setLiveCount] = useState(null);
  const onSelectionChanged = useCallback((state, meta = {}) => {
    const seq = Number(meta.seq || 0);
    if (seq && seq < selSeq.current) return;      // an older frame, arriving late
    selSeq.current = seq || selSeq.current;
    const hasIdentity = state && Object.prototype.hasOwnProperty.call(state, "uniqueIds");
    // While the rectangle is still being drawn: the count only. The list
    // is left exactly as it was - a selection that is still changing has
    // nothing a table could usefully show.
    setLiveCount(meta.inProgress ? Number(state && state.count) || 0 : null);
    if (meta.inProgress || !hasIdentity) return;
    // Release: the identity is here, so this is a real selection.
    const uids = (state.uniqueIds || []).filter(Boolean);
    // A BOX IS NOT A PARTS SELECTION, for the same reason a click is not.
    setPartsFrom("");
    setSelected(uids);
    // ...and it supersedes a level pick, for the same reason the box does.
    // Only a NON-EMPTY selection does: an empty release is a deselect-all and
    // must not silently throw away a level he chose deliberately.
    if (uids.length) setLevelPick([]);
    // uniqueIds.length < count IS the cap, self-describing (Paul): no
    // separate flag to fall out of step with the numbers.
    const total = Number(state.count);
    if (Number.isFinite(total) && uids.length && total > uids.length) {
      message.info(`${total.toLocaleString()} elements are selected; the first ${uids.length.toLocaleString()} are listed`);
    }
  }, []);

  const pickElement = useCallback(async (uid, additive = false) => {
    // A PLAIN CLICK REPLACES THE SELECTION; A CTRL-CLICK ADDS TO IT.
    // This is the guard that lets the selection be the thing that gets
    // acted on and printed without reviving what the owner ruled against
    // at 20:35: his objection was to ACCUMULATION - a stray click
    // silently swelling what gets written - not to clicks being
    // actionable. So a plain click clears the quantities ticks and the
    // level pick too, and what is left is exactly the one element.
    // A MISS CLEARS THE TICKS TOO. A ctrl-click used to be exempt from this
    // because ctrl means "add to what I have" - but a ctrl-click on NOTHING is
    // not an add, it is the owner asking for an empty slate (2026-09-30), so
    // the ticks, the level pick and the click selection all go.
    if (!uid || !additive) { setRecordChecked([]); }
    // THE LEVEL PICK GOES EITHER WAY (owner, 2026-09-28). A plain click
    // already cleared it; a ctrl-click did not, so ctrl-clicking elements
    // while Level 1 was picked left the panel reporting Level 1's elements and
    // never his. Ctrl-click still ACCUMULATES the click selection - that is
    // what it is for - but the level pick is a different, older act and a new
    // pick supersedes it.
    setLevelPick([]);
    // A NESTED PART IS NOT A THING ON ITS OWN. A trapeze, a hanger or a
    // panelboard is drawn as its nested pieces - the washers, nuts, rods and
    // struts - and each piece is its own instance row with its own uid and NO
    // commit row. So clicking a trapeze sent the washer's id, and the board,
    // which counts the parent, answered "that element is not in the current
    // model" and blamed a publish mismatch (owner, 2026-10-03). Measured on
    // the live BA03 export: of 400 part/parent pairs in one tile, 0 parts are
    // in the commit and 400 parents are, 376 of them SP_Trapeze_BES_MultiTier.
    //
    // The mapping is the exporter's own (the sidecar header's parents table,
    // learned per tile as it lands - ./../../webgl/js/loader/streaming/nestedParts),
    // so a part is identified POSITIVELY and never from "this uid has no row":
    // absence is also what a failed fetch and an older export look like.
    // pages/ModelViewer does this on its own click path; the tracking page
    // never did, which is the whole of why the same click worked there.
    //
    // Resolved HERE, before the selection is set, so everything downstream -
    // the panel, the bulk bar, a status write, the lists - acts on the element
    // the board counts rather than on a washer.
    const picked = uid ? resolveToElement(uid) || uid : uid;
    // A CLICK IS NOT A PARTS SELECTION. Clicking resolves a part to its
    // element (above), so what lands here is an element - and leaving the
    // previous custom category's id set would tell the server to treat
    // these ids as that category's parts.
    setPartsFrom("");
    setSelected((prev) => nextSelection(prev, picked, additive));
    if (!uid) {
      setPicked(null);
      // SAY WHY, WHEN THE FILTER IS WHAT SWALLOWED THE CLICK.
      //
      // Owner, 2026-09-30: "the selection panel on top of the 3D view stopped
      // popping up", and then "yes when i turn off select filtered only, the
      // panels shows correctly". It was never the panel. Select filtered only
      // HIDES everything outside the filter, and the viewer drops raycast hits on
      // hidden items (collapsedItemHit) - so those clicks resolved to nothing, and
      // a refusal that says nothing reads as a broken feature.
      //
      // Only when a filter is actually hiding something. An ordinary click on
      // empty space clears and stays silent, which is what it is for.
      // WHY NOTHING WAS PICKED - every reason, not just one of them.
      //
      // The viewer drops raycast hits on hidden rows, so ANYTHING that
      // hides makes a click land on nothing. This used to speak only for
      // "Select filtered only", so the other causes read as a broken
      // panel - which is exactly what the owner reported twice
      // (2026-09-30 and again 2026-10-05). A click on genuinely empty
      // space still says nothing, because that is what it is for.
      const why = [];
      if (filteredOnly && quantityFilters && filterUids.length) {
        why.push('"Select filtered only" is on, so everything outside the filter is hidden');
      }
      if (onlySelected && drawKeys.length) {
        why.push('"Tracked elements" is on, so only the categories ticked in Model elements are shown');
      }
      if (isolateUids && isolateUids.length && cutLevels.length) {
        why.push(`this page is cut to ${cutLevels.join(", ")}`);
      }
      if (why.length) {
        message.info(`Nothing was picked: ${why.join(", and ")}. Clicks land on nothing where the model is hidden.`);
      }
      return;
    }
    const seq = (pickSeq.current += 1);
    setPickLoading(true);
    try {
      const r = await getElement(projectId, picked);
      // Clicks come faster than the answers do; only the newest one is
      // still the thing on screen.
      if (pickSeq.current === seq) setPicked(r.element || null);
    } catch (e) {
      if (pickSeq.current === seq) setPicked({ uniqueId: picked, missing: e.message || "Not in the current model" });
    } finally {
      if (pickSeq.current === seq) setPickLoading(false);
    }
    // EVERY READ IN THE "WHY NOTHING WAS PICKED" BRANCH IS IN THE DEPS, for
    // the reason this comment already gave about the filter: a callback
    // built when the filter was empty keeps saying nothing. I added the
    // isolate and the page cut to that branch and left them out of here,
    // and the closure kept drawKeys as the empty array it was at mount -
    // so the new message never fired once. Caught by its own test, one
    // line under the comment warning about it.
  }, [projectId, filteredOnly, quantityFilters, filterUids,
    onlySelected, drawStamp, isolateUids, cutLevels]);

  useEffect(() => {
    let alive = true;
    // A page that cuts by level narrows the model to that level - the
    // whole level, tracked or not: that IS the page.
    if (!projectId || !cutLevels.length) {
      // DROPPING THE CUT DROPS WHAT IT RESOLVED TO - all of it (owner,
      // 2026-10-05: "view config sections is not refreshing the fragments in
      // view with correct data").
      //
      // This cleared the VIEWER's cut and left the DATA side's behind.
      // cutUidsId is the selection the elevation switches resolve to, and it
      // is read in six places - the stats, the base selection every write
      // narrows by, the export and the quantities table twice. So changing
      // the View Config section from "Chosen levels" to "A section between
      // two heights", or to "The whole model", opened the model up in 3D and
      // left the table and the numbers narrowed by the PREVIOUS section's
      // selection. The view refreshed; the data did not, which is exactly
      // what he reported.
      //
      // elevationInfo goes with it: the note says "N elements are at this
      // elevation and the model is drawing them", and the model is not
      // drawing them any more.
      setIsolateUids(null);
      setCutUidsId("");
      setElevationInfo({ note: "", added: 0, range: null });
      return undefined;
    }
    setIsolating(true);
    // JOINED WITH THE PIPE, not the comma: level names carry commas and
    // quotes (CUP - Level 1 = 97'-6"), and the server splits on the pipe
    // when it sees one. Sent as a comma list, a name with a comma in it
    // isolated the wrong elements or none.
    // A FAILED READ MUST NOT OPEN THE MODEL UP. This used to end in
    // setIsolateUids(null), which means "no cut": one failed request and
    // the page meant to show Level 3 quietly drew every level in the
    // building, which is the opposite of what the page says it is (owner,
    // 2026-09-28: "when pages change the correct level fragments need to
    // show, and the other levels fragments need to hide"). Retry once,
    // and if that fails keep the cut that is on screen and SAY the page
    // could not be read rather than showing the wrong floors silently.
    // AT THIS ELEVATION TOO, when the page asks for it (owner,
    // 2026-10-02). The server adds the elements that MEET the band these
    // levels occupy whatever level they are tagged to, into the same uid
    // list - so everything downstream of this effect is unchanged - and
    // answers with the band it used and how many it added. That answer is
    // kept and shown, because a switch that quietly added nothing (no
    // built positions, no live version) would look exactly like a switch
    // that worked.
    const ask = () => getScopeUids(projectId, {
      all: 1, levels: cutLevels.join("|"), documentIds: prefixFilter,
      ...(atElevation ? { atElev: 1 } : {}),
      ...(untaggedAtElevation ? { noLevelAt: 1 } : {}),
      ...(atElevation || untaggedAtElevation ? { elevBelow: elevBelow, elevAbove: elevAbove } : {}),
    });
    ask()
      .catch(() => new Promise((resolve) => setTimeout(resolve, 800)).then(ask))
      .then((r) => {
        if (!alive) return;
        const e = r && r.elevation;
        setElevationInfo(e ? { note: e.note || "", added: e.added || 0, range: e.range || null } : { note: "", added: 0, range: null });
        const uids = r.uids || [];
        // THE EXTRAS ARE THE TAIL. The route writes the level's own uids
        // first and the elevation extras after, and reports where the
        // join is as `onLevelCount` - so slicing is exact and costs
        // nothing, where sending the list twice would cost its size.
        const extras = extrasOf(r);
        if (extras.length) {
          createQuantitySelection(projectId, extras)
            .then((sel) => { if (alive) setCutUidsId(String((sel && sel.selectionId) || "")); })
            .catch((err) => {
              if (!alive) return;
              // A REFUSAL IS NOT AN EMPTY CUT. If the extras cannot be
              // registered the table would silently go back to matching
              // level names - showing fewer elements than the model draws,
              // which is the exact fault this whole change repairs. So the
              // id is cleared AND the panel says why.
              setCutUidsId("");
              setElevationInfo((prev) => ({
                ...prev,
                note: `${extras.length.toLocaleString()} elements are at this elevation and the model is drawing them, but they could not be added to the quantities table (${err.message || "the selection was refused"}). The table and the status buttons are showing the tagged levels only.`,
              }));
            });
        } else {
          setCutUidsId("");
        }
        // No element on these levels: hide everything rather than fall
        // back to the whole model. An empty level is an empty page.
        setIsolateUids(uids.length ? uids : ["__nothing__"]);
      })
      .catch(() => {
        if (!alive) return;
        // Keep the cut that is on screen; with nothing on screen yet,
        // draw NOTHING rather than every floor. A page that says "Level
        // 3" and shows the whole building is a wrong answer; a page that
        // shows nothing and says why is a missing one.
        setIsolateUids((prev) => prev || ["__nothing__"]);
        message.error(`Could not read the levels for this page (${cutLevels.join(", ")}). The model is not showing this cut - reload to try again.`);
      })
      .finally(() => { if (alive) setIsolating(false); });
    return () => { alive = false; };
  }, [projectId, cutLevels, prefixFilter, atElevation, untaggedAtElevation, elevBelow, elevAbove]);

  // SAVE THE FILTER ON THE PAGE (owner, 2026-09-29: "filter should be saved
  // by page ... in 3d construction tracking"). The page already remembers its
  // levels, its sheet, its camera and its files; since the filter decides what
  // the page DRAWS in 3D, a page that forgets it reopens showing something
  // nobody chose.
  //
  // Debounced, because a filter arrives keystroke by keystroke through the name
  // box, and compared against what the page already holds so that merely
  // OPENING a page - which seats the filter and fires onFiltersChange once -
  // cannot write the same value back and take the page's lastEditedBy with it.
  // WHICH PAGE THE FILTER ON SCREEN BELONGS TO. The tree sends it with the
  // filter; until it matches the active page, the filter in hand is the
  // previous page's and must not be written to this one.
  const [filtersForPage, setFiltersForPage] = useState("");
  // SWITCHING ON AN ELEVATION SWITCH CLEARS THE PAGE'S LEVEL FILTER
  // (owner, 2026-10-02: "Switching on clears the level filter").
  //
  // He chose this over the two alternatives knowing it discards something
  // he typed - so it is a decision, not an oversight. But a page that
  // quietly drops a hand-set filter is the shape we have fixed four times
  // today, so it is SAID at the moment it happens and the header does not
  // keep showing a chip for a clause that is gone.
  //
  // IT IS NOT PUT BACK when the switch goes off, and the switch says so.
  // "Clears" that secretly means "parks" is hidden state, and a filter
  // restored against a model whose levels have changed since is a stale
  // value nobody asked for. The sentence on the switch is the price of
  // that choice and it is cheaper than the surprise.
  const [filterSeat, setFilterSeat] = useState(0);
  const onQuantityFilters = useCallback((f, forPage) => {
    setQuantityFilters(f);
    setFiltersForPage(String(forPage === undefined || forPage === null ? "" : forPage));
  }, []);
  const savedFilterRef = useRef(null);
  useEffect(() => {
    if (!projectId || !activePage || !canEdit) return undefined;
    const pageId = String(activePage._id);
    // THE FILTER IN HAND MUST BE THIS PAGE'S. On a page switch the active page
    // changes one render before the tree re-seats its headers, so without this
    // the previous page's filter was saved onto the new one - which is how
    // Page 1 (Level 1) came to hold levels:["Level 2"] and show Page 2's
    // numbers. Not a debounce problem: no delay fixes a value that belongs to
    // something else.
    // An UNSTAMPED filter is not saved either, deliberately: the tree always
    // stamps, so an unstamped one means a caller this guard has never seen,
    // and refusing to write is the safe side of that - a wrong filter saved
    // to a page is what this whole fix is about.
    if (filtersForPage !== pageId) return undefined;
    const mine = JSON.stringify(quantityFilters || null);
    // What the page holds, through the same door the tree reads it by, so the
    // two cannot disagree about "no filter" (null vs an all-empty object).
    const theirs = JSON.stringify(forServer(fromSaved(activePage.filters)) || null);
    if (mine === theirs) { savedFilterRef.current = `${pageId}:${mine}`; return undefined; }
    if (savedFilterRef.current === `${pageId}:${mine}`) return undefined;
    const t = setTimeout(async () => {
      savedFilterRef.current = `${pageId}:${mine}`;
      try {
        const r = await updatePage(projectId, pageId, { filters: quantityFilters || null });
        setPages((prev) => prev.map((x) => (String(x._id) === String(r.page._id) ? r.page : x)));
      } catch (e) {
        // SAY IT. A filter the person believes is saved and is not is the
        // silent-degradation shape: the page reopens on something else and
        // nothing ever said why.
        savedFilterRef.current = null;
        message.error(e.message || "Could not save this page's filter");
      }
    }, 900);
    return () => clearTimeout(t);
  }, [projectId, activePage, activePageId, canEdit, quantityFilters, filtersForPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLists = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await getLists(projectId);
      setLists(r.lists || []);
    } catch (e) { /* the board still works without lists */ }
  }, [projectId]);
  useEffect(() => { loadLists(); }, [loadLists]);

  const loadPages = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await getPages(projectId);
      const list = r.pages || [];
      setPages(list);
      setActivePageId((prev) => (prev && list.some((x) => String(x._id) === String(prev))
        ? prev : String(list[0]?._id || "")));
    } catch (e) { /* the model still opens without a saved page */ }
  }, [projectId]);
  useEffect(() => { loadPages(); }, [loadPages]);

  useEffect(() => {
    let alive = true;
    if (!projectId) return undefined;
    getLevels(projectId, { documentIds: prefixFilter })
      .then((r) => { if (alive) setLevels(r.levels || []); })
      .catch(() => { if (alive) setLevels([]); });
    return () => { alive = false; };
  }, [projectId, prefixFilter]);

  // Saving writes to the ACTIVE page, or creates the first one. A person
  // aligning a model has not necessarily thought about pages yet.
  stage.current.save = async (body) => {
    try {
      const r = activePage
        ? await updatePage(projectId, activePage._id, body)
        : await createPage(projectId, { name: "Page 1", ...body });
      setPages((prev) => {
        const next = prev.filter((x) => String(x._id) !== String(r.page._id));
        return [...next, r.page].sort((a, b) => (a.order || 0) - (b.order || 0));
      });
      setActivePageId(String(r.page._id));
      message.success("Alignment saved");
    } catch (e) {
      message.error(e.message || "Could not save the alignment");
    }
  };
  // SAVE ON ALL LEVELS (owner, 2026-10-13: "a button next to save alignment,
  // that says save on all levels, that aligns both model and sheet the same
  // size and locations for both, while keeping elements, level heights for
  // that level"). This page is saved as usual, then every other page gets the
  // same drawing, scale, turn and plan position, and the same model move.
  // Each page KEEPS its own levels, its own sheet height (the floor it is
  // drawn at) and its own camera - only where the sheet sits in plan moves.
  stage.current.saveAll = async (body) => {
    await stage.current.save(body);
    const here = activePage ? String(activePage._id) : "";
    const others = pages.filter((p) => String(p._id) !== here);
    const done = []; const failed = [];
    for (const p of others) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await updatePage(projectId, p._id, alignmentForPage(body, p));
        done.push(r.page);
      } catch (e) {
        failed.push(p.name || "a page");
      }
    }
    if (done.length) {
      setPages((prev) => prev.map((x) => done.find((d) => String(d._id) === String(x._id)) || x));
    }
    if (failed.length) message.error(`Saved here and on ${done.length} other page${done.length === 1 ? "" : "s"}; could not save ${failed.join(", ")}`);
    else message.success(`Alignment saved on all ${done.length + 1} pages, each at its own level height`);
  };

  const onCreateList = async () => {
    const name = listName.trim();
    if (!name) { message.warning("Give the list a name"); return; }
    // A SET IS A SET OF ELEMENTS, and a custom category stands for PARTS
    // inside elements. Rather than put the ticked definition in and have
    // the set quietly mean its parent hangers - a different population
    // with a different count - say which tick cannot go in, and let the
    // rest of the pick through.
    const derivedTicked = nodesForKeys(recordChecked).derivedIds;
    if (derivedTicked.length && !checked.length && !listSeed.length
      && !nodesForKeys(recordChecked).recordNodes.length) {
      message.warning("A set holds elements, and a custom category stands for the parts inside them - tick the categories or elements you want in the set, or select the parts in the model and save those.");
      return;
    }
    if (derivedTicked.length) {
      message.info(`The set is being made from the rest of the pick; ${derivedTicked.length === 1 ? "the ticked custom category stands for parts rather than elements, so it is" : "the ticked custom categories stand for parts rather than elements, so they are"} not in it.`);
    }
    if (!checked.length && !recordChecked.length && !listSeed.length) { message.warning("Tick some rows in the tree first"); return; }
    try {
      const r = await createList(projectId, {
        name,
        ...(listSeed.length ? { uniqueIds: listSeed } : {}),
        nodeKeys: minimalKeys(checked),
        ...(exclusionKeys(excluded).length ? { exclude: exclusionKeys(excluded) } : {}),
        // Rows ticked in the Detailed tree are RECORD rows; the set keeps
        // them in that tree's own words so it re-resolves after the next
        // publish exactly as a pick from Model elements does.
        recordNodes: nodesForKeys(recordChecked).recordNodes,
        documentIds: prefixFilter,
      });
      message.success(`"${name}" · ${r.list.count} element${r.list.count === 1 ? "" : "s"}`);
      setListOpen(false);
      setListName("");
      setChecked([]);
      setRecordChecked([]);
      setListSeed([]);
      await loadLists();
    } catch (e) {
      message.error(e.message || "Could not create the set");
    }
  };

  // Put keys into the tracked scope from the model view - an element, a
  // type, a category, or a gathered selection - the same way a tick in
  // the tree does, so they are saved and painted alike.
  const trackKeys = (keys) => {
    const add = (keys || []).filter(Boolean);
    if (!add.length) return;
    userTouched.current = true;
    setChecked((prev) => Array.from(new Set([...(prev || []), ...add])));
    message.success(`${add.length === 1 ? "Added to" : `${add.length} added to`} the tracked scope`);
  };

  // One status, one date, many elements: the gathered selection, or a
  // whole type or category resolved on the server.
  // `status` explicit: the bulk bar picks one and applies it in the same
  // breath, and reading it back off state would apply the PREVIOUS one.
  const applyBulk = async (target, status = null, { what = "" } = {}) => {
    const use = status || bulkStatus;
    setBulkBusy(true);
    try {
      const body = {
        ...target, status: use, date: dateForStatus(use, statusDate, projectedDate),
        documentIds: prefixFilter || undefined,
        // Only with an id list: a node or a filter never stands for parts,
        // and sending it there would be a claim about rows nobody selected.
        ...(partsFrom && Array.isArray(target.uniqueIds) && target.uniqueIds.length
          ? { partsFrom } : {}),
        // Whatever bar pressed it, a status on this page stays on its levels.
        ...(cutLevels.length ? { pageLevels: cutLevels } : {}),
        // ...and so does the elevation half, or a status pressed on this
        // page would skip the elements the page is showing because of it.
        ...(cutUidsId ? { cutUids: cutUidsId } : {}),
        // What was dropped out of the ticked branch goes with it: the
        // number previewed is the number written.
        ...(exclusionKeys(excluded).length ? { exclude: exclusionKeys(excluded) } : {}),
      };
      // ANYTHING THAT IS NOT ONE ELEMENT SAYS HOW MANY IT WILL CHANGE
      // FIRST. It used to be "a node, but not a list of ids", which was
      // right while ids only ever came from clicking one thing - and
      // stopped being right the moment a LEVEL PICK could hand this a
      // thousand of them with no question asked.
      // Everything except ONE named element asks first - see
      // statusBulk.needsConfirmation for the day the old rule let a
      // select-all write straight through.
      const r = needsConfirmation(target)
        ? await setStatusWithPreview(projectId, body, { what }) : await setElementStatus(projectId, body);
      if (!r) return;
      announceStatus(projectId, r, use, () => { loadTracked(); loadStats(); statusWritten(); });
      statusWritten();
      // The server names what it changed (up to 50k), so the model recolours without a reload.
      const changedUids = target.uniqueIds || r.uniqueIds;
      if (changedUids) {
        setStatusByUid((prev) => {
          const next = new Map(prev);
          changedUids.forEach((u) => next.set(u, use));
          return next;
        });
        if (picked && changedUids.includes(picked.uniqueId)) {
          setPicked((prev) => (prev ? { ...prev, status: use } : prev));
        }
      } else {
        loadTracked();
      }
      loadStats();
    } catch (e) {
      if (!e.cancelled) message.error(e.message || "Could not set the status");
    } finally {
      setBulkBusy(false);
    }
  };

  // The layouts the export leads with (owner, 2026-09-18): the current
  // view, the whole model from above, then one plan per level - every
  // element without a status in grey halftone, the tracked ones in their
  // status colour, the legend drawn in. Captured here, from the model the
  // person already has loaded; a level's plan isolates that level's
  // elements for one frame.
  const MAX_LEVEL_PLANS = 12;
  const MAX_SECTIONS = 24;
  // A3 landscape at ~210 dpi: a plan rendered at this size fills its sheet
  // with every element legible (owner, 2026-09-22: "make the layout as big
  // as possible on 1 page to show all details of all elements/fragments and
  // their colors and legend").
  const PLAN_SIZE = { width: 3508, height: 2480 };
  const exportScopeQuery = () => ({ scope: scopeKeys.length ? 1 : undefined, nodeKeys: scopeKeysCsv || undefined, documentIds: prefixFilter || undefined });
  // WHAT THE EXPORT LEADS WITH AND THEN GOES THROUGH, PAGE BY PAGE (owner,
  // 2026-09-22): the whole model's plan and the current view, then one
  // section per tracking PAGE that is set to levels - its numbers and its
  // plan - or, on a project without such pages, one per model level.
  const captureLayouts = async () => {
    const cap = stage.current.captureLayoutRef;
    if (!cap) return { views: [], sections: [] };
    const byStatus = (stats && stats.overall && stats.overall.byStatus) || {};
    const stamp = new Date().toISOString().slice(0, 10);
    const views = [];
    const say = (content) => message.loading({ content, key: "export-progress", duration: 0 });
    const compose = (img, title, subtitle, counts, big) => composeLayout(img, {
      title, subtitle: `${project?.title || ""} · ${stamp}${subtitle ? ` · ${subtitle}` : ""}`, byStatus: counts, ...(big ? { maxWidth: PLAN_SIZE.width, quality: 0.9 } : {}),
    }).catch(() => null);
    say("Capturing the whole model…");
    const whole = await compose(cap({ top: true, size: PLAN_SIZE }), "Whole model - plan", "", byStatus, true);
    if (whole) views.push({ title: "Whole model - plan", image: whole });
    const current = await compose(cap({ top: false }), "Current view", "", byStatus, false);
    if (current) views.push({ title: "Current view", image: current });

    const leveled = pages.filter((pg) => pg.cut && pg.cut.mode === "level" && (pg.cut.levelNames || []).length);
    // THE PAGE'S CUT TRAVELS WITH ITS TITLE. It used to carry only the
    // level names, which is why the printed section could not have the
    // elevation half even in principle.
    let targets = leveled.map((pg) => ({
      title: pg.name || "Page", levels: pg.cut.levelNames, pageId: String(pg._id), cut: pg.cut,
    }));
    if (!targets.length) {
      // No page set to levels: one section per level that HOLDS TRACKED
      // elements - the model's biggest levels could be all walls, and a
      // section reading "0 elements" is noise, not a page of the job.
      const withTracked = await getElementStats(projectId, { ...exportScopeQuery(), listId: activeList || undefined, byLevel: 1 }).catch(() => null);
      targets = ((withTracked && withTracked.byLevel) || [])
        .filter((l) => l && l.level && l.level !== "(no level)" && l.total > 0)
        .map((l) => ({ name: l.level, count: l.total }))
        .sort(levelOrder).slice(0, MAX_LEVEL_PLANS)
        .map((l) => ({ title: l.name, levels: [l.name] }));
    }
    targets = targets.slice(0, MAX_SECTIONS);
    const sections = [];
    const q = exportScopeQuery();

    // EVERY PAGE'S ELEVATION HALF, RESOLVED ONCE, BEFORE ANY COMPOSING
    // (owner, 2026-10-02: fix it for all pages).
    //
    // One call for all the pages, because the cost is the LOAD and not the
    // work - 2,230 ms and 194 MB on BA03, against 253 ms of arithmetic per
    // page afterwards. And BEFORE the compose, deliberately: the server
    // drops the boxes when the batch answers, so a fifth of a gigabyte is
    // not held while this process is also building PDFs.
    const elevAsk = elevationAsk(targets);
    let elevRows = [];
    if (elevAsk.length) {
      say(`Working out what stands at ${elevAsk.length === 1 ? "one page's" : `${elevAsk.length} pages'`} elevations…`);
      try {
        const r = await getPagesCutUids(projectId, { pages: elevAsk, documentIds: prefixFilter || undefined });
        elevRows = (r && r.pages) || [];
      } catch (e) {
        // A FAILED BATCH IS NAMED, NOT SWALLOWED. Every page that asked is
        // marked tagged-only with the reason, so the printed export says
        // what it is showing instead of looking complete.
        elevRows = askFailedRows(elevAsk, e.message);
      }
    }
    // What the whole export has to admit.
    const taggedOnly = taggedOnlyFrom(targets, elevRows);
    for (let i = 0; i < targets.length; i += 1) {
      const t = targets[i];
      const lvKey = t.levels.join("|");
      // This page's elevation half, if it asked for one and it resolved.
      const elevId = cutUidsFor(elevRows, t.pageId);
      say(`${t.title}: numbers and plan (${i + 1} of ${targets.length})…`);
      // eslint-disable-next-line no-await-in-loop
      const [u, st, cats] = await Promise.all([
        // STATED, NOT SILENT: this loop walks the OTHER pages of the
        // export, each with its own level list, and does NOT resolve their
        // elevation extras - doing so would mean a boxes pass and a
        // selection registration per page inside a loop that already runs
        // for minutes. So an exported plan of a page with the elevation
        // switches on shows that page's TAGGED levels only. The live page
        // on screen is complete; the printed sheet of a different page is
        // the tagged half. Worth fixing when somebody asks for it, and
        // worth saying until then.
        getScopeUids(projectId, { all: 1, levels: lvKey, documentIds: prefixFilter, ...(elevId ? { cutUids: elevId } : {}) }).catch(() => null),
        getElementStats(projectId, { ...q, listId: activeList || undefined, pageLevels: lvKey, ...(elevId ? { cutUids: elevId } : {}) }).catch(() => null),
        getStatusTotals(projectId, { ...q, by: "category", pageLevels: lvKey, ...(elevId ? { cutUids: elevId } : {}) }).catch(() => null),
      ]);
      const uids = (u && u.uids) || [];
      const counts = (st && st.overall && st.overall.byStatus) || {};
      let image = null;
      if (uids.length) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 30));
        // eslint-disable-next-line no-await-in-loop
        image = await compose(cap({ top: true, isolate: uids, size: PLAN_SIZE }), `${t.title} - plan`, `${t.levels.join(", ")} · ${uids.length.toLocaleString()} elements`, counts, true);
      }
      sections.push({ title: t.title, levels: t.levels, image, stats: st ? { overall: st.overall } : null, categoryTotals: cats });
    }
    // WHICH PAGES ARE TAGGED-ONLY, carried out with the sections so the
    // printed export can say so. A section that quietly shows less than
    // the model is the bug; the fallback is only acceptable when it is
    // named (team rule: degrade to today, and SAY so).
    if (taggedOnly.length) {
      message.warning(
        `${taggedOnly.length} page${taggedOnly.length === 1 ? "" : "s"} printed with tagged levels only: `
        + taggedOnly.map((x) => x.title).join(", "),
        10,
      );
    }
    return { views, sections, taggedOnly };
  };

  // The tables: root-8e's status totals by category and by type (every
  // leaf; the server adds the family and category subtotals) and the
  // tracked buckets - the same scope and files as the tree.
  const exportTables = async () => {
    const q = exportScopeQuery();
    const [categoryTotals, leafTotals, buckets, allLevels] = await Promise.all([
      getStatusTotals(projectId, { ...q, by: "category" }).catch(() => null),
      getStatusTotals(projectId, { ...q, by: "type" }).catch(() => null),
      getTrackedBuckets(projectId).catch(() => null),
      // Every level, for the summary on top: counts, statuses, %, length.
      getElementStats(projectId, { ...q, listId: activeList || undefined, byLevel: 1 }).catch(() => null),
    ]);
    return { categoryTotals, leafTotals, buckets: buckets && buckets.status === "ok" ? buckets : null, allLevels };
  };

  const runExport = async (kind) => {
    // THE CSV IS THE ROWS, NOTHING ELSE: no plans to capture or totals to
    // add, so it goes straight out with the same scope as the other two.
    if (kind === "csv") {
      setExporting(kind);
      try {
        await exportCsv(projectId, {
          scope: scopeKeys.length ? 1 : undefined,
          nodeKeys: scopeKeys.length ? scopeKeys : undefined,
          documentIds: prefixFilter,
          listId: activeList || undefined,
          listName: lists.find((l) => String(l._id) === String(activeList))?.name || "",
          filterLabel: filterLabel(documents, documentIds) || "all linked files",
        }, `construction-tracking-${new Date().toISOString().slice(0, 10)}.csv`);
        message.success("CSV exported - every element in scope");
      } catch (e) {
        message.error(e.message || "Export failed");
      } finally {
        setExporting("");
      }
      return;
    }
    // Opened NOW, on the click: after the capture's wait it would be a popup.
    const printWin = kind === "print" ? window.open("", "_blank") : null;
    if (printWin) { try { printWin.document.title = "Preparing the print…"; printWin.document.body.innerHTML = "<p style='font:14px sans-serif;padding:24px'>Preparing the construction tracking print…</p>"; } catch (e) { /* cross-origin never; best effort */ } }
    setExporting(kind);
    try {
      const { views, sections } = await captureLayouts().catch(() => ({ views: [], sections: [] }));
      message.loading({ content: "Adding the totals…", key: "export-progress", duration: 0 });
      const { allLevels, ...tables } = await exportTables();
      const canvas = views.length ? null : (stage.current.captureRef ? stage.current.captureRef(2) : null);
      // The plan - the model from above in its status colours - leads the
      // PDF and sits first in the workbook.
      const topView = views.length ? null : (stage.current.captureTopViewRef ? stage.current.captureTopViewRef(2) : null);
      const charts = renderStatsChart(stats);
      const body = {
        ...tables,
        views,
        sections,
        // The summary on top: the board's numbers plus every level.
        stats: allLevels ? { ...(stats || {}), overall: allLevels.overall || (stats && stats.overall), byLevel: allLevels.byLevel } : stats,
        canvas,
        topView,
        charts,
        // "All tracked elements": the project's scope, with the quantities
        // per type the server derives from it. Without a scope the export
        // is the whole model, as before.
        scope: scopeKeys.length ? 1 : undefined,
        nodeKeys: scopeKeys.length ? scopeKeys : undefined,
        documentIds: prefixFilter,
        listId: activeList || undefined,
        listName: lists.find((l) => String(l._id) === String(activeList))?.name || "",
        // COUNTED AGAINST WHAT IS ACTUALLY THERE. `documents` now excludes
        // the files switched off on the project card, so M is right - but
        // a pick SAVED before a file was switched off would still be in
        // documentIds and give "3 of 2 linked files". Both halves are
        // counted from the same visible set, so the label cannot exceed
        // itself. A number that disagrees with what is drawn is the same
        // bug as a list that does (leader, 2026-09-20).
        filterLabel: filterLabel(documents, documentIds) || "all linked files",
      };
      const stamp = new Date().toISOString().slice(0, 10);
      const base = `construction-tracking-${stamp}`;
      if (kind === "xlsx") await exportXlsx(projectId, body, `${base}.xlsx`);
      else if (kind === "print") await openPdf(projectId, body, printWin);
      else await exportPdf(projectId, body, `${base}.pdf`);
      message.destroy("export-progress");
      message.success(views.length || sections.length
        ? `Exported: all levels, then ${sections.length} page${sections.length === 1 ? "" : "s"} with their plans`
        : `Exported${canvas ? " with the model view" : " (the model view could not be captured)"}`);
    } catch (e) {
      message.destroy("export-progress");
      message.error(e.message || "Export failed");
      if (printWin && !printWin.closed) printWin.close();
    } finally {
      setExporting("");
    }
  };

  const onSyncSchedule = async () => {
    try {
      const r = await syncToSchedule(projectId, { applyDates: true });
      message.success(`${(r.activities || []).length} activity(ies) updated from the projected dates`);
    } catch (e) {
      message.error(e.message || "Could not sync to the schedule");
    }
  };

  // THE BULK ACTIONS, shared by the element view and the selection view below.
  const bulkNode = canEdit && (selected.length > 1 || keysForElement(picked).type) ? (
    <div style={{ marginBottom: 10, padding: 8, background: "#FAFAFA", border: `1px solid ${T.line}`, borderRadius: 6 }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 6 }}>
        BULK · {selected.length} selected{selected.length > 1 ? " (ctrl-click adds more)" : " · ctrl-click to add more"}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <Select getPopupContainer={popupInStage}
          size="small"
          style={{ minWidth: 150 }}
          value={bulkStatus}
          onChange={setBulkStatus}
          options={STATUS_ORDER.map((v) => ({ value: v, label: STATUS_LABEL[v] || v }))}
        />
        {selected.length > 1 && (
          <Button size="small" type="primary" loading={bulkBusy} onClick={() => applyBulk({ uniqueIds: selected })}>
            Mark {selected.length} selected
          </Button>
        )}
        {keysForElement(picked).type && (
          <Button size="small" loading={bulkBusy} onClick={() => applyBulk({ nodeKeys: [keysForElement(picked).type] })}>
            Mark all of this type
          </Button>
        )}
        {keysForElement(picked).category && (
          <Button size="small" loading={bulkBusy} onClick={() => applyBulk({ nodeKeys: [keysForElement(picked).category] })}>
            Mark all {picked.category}
          </Button>
        )}
        {canEditScope && selected.length > 1 && (
          <Button size="small" onClick={() => trackKeys(selected.map((u) => `element:${u}`))}>Track {selected.length} selected</Button>
        )}
        {selected.length > 0 && (
          <Button size="small" type="text" onClick={() => setSelected([])}>Clear selection</Button>
        )}
      </div>
    </div>
  ) : null;

  // What was clicked in the model, docked over the canvas so it is there
  // in full screen too: the element's properties and its status, which
  // can be moved from here - the model recolours on the same change.
  //
  // MORE THAN ONE SELECTED KEEPS THE PANEL (owner, 2026-10-14: "when we ctrl
  // select multiple items, the model view doesn't show the floating
  // properties panel, it only works when we select a single item, in small
  // view and full screen view"). The panel was keyed on `picked` - the last
  // CLICKED element - alone, and a ctrl-drag box never sets it, so a
  // multi-selection made that way had no panel at all, and closing it on one
  // element hid the bulk actions for all of them. With two or more selected
  // it now stays: the count and the bulk actions always, the last clicked
  // element's properties when there is one. Close hides it for THIS
  // selection only; the next change of selection brings it back.
  const selectionKey = selected.length > 1 ? `${selected.length}:${selected[0]}:${selected[selected.length - 1]}` : "";
  const multiOpen = !!selectionKey && panelClosedFor !== selectionKey;
  const panelNode = (picked || pickLoading || multiOpen) ? (
    <Card
      size="small"
      bordered={false}
      title={pickLoading && !picked ? "Reading the element…" : picked ? "Selected element" : `${selected.length.toLocaleString()} selected`}
      extra={<Button size="small" type="text" onClick={() => { setPicked(null); setPanelClosedFor(selectionKey); }}>Close</Button>}
      bodyStyle={{ padding: 12 }}
      headStyle={{ minHeight: 36 }}
    >
      {!picked ? (pickLoading ? <Spin size="small" /> : (
        <>
          {bulkNode || (
            <div style={{ fontSize: 12.5, color: T.ink, marginBottom: 8 }}>
              {selected.length.toLocaleString()} elements selected.
            </div>
          )}
          <div style={{ fontSize: 11.5, color: T.muted }}>
            Click an element to see its properties here; ctrl-click adds it to the selection.
          </div>
        </>
      )) : picked.missing ? (
        <div style={{ fontSize: 12.5, color: T.muted }}>
          {picked.missing}. The model in the viewer may be a different publish from the one the
          board is counting.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{
              width: 12, height: 12, borderRadius: 3, flexShrink: 0,
              background: paintByUid.has(picked.uniqueId) ? STATUS_COLOR[picked.status] || "#ccc" : "#d9d9d9",
            }} />
            <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, flex: 1, minWidth: 0 }}>
              {picked.typeName || picked.family || picked.category || "Element"}
            </span>
          </div>
          {!paintByUid.has(picked.uniqueId) && scopeKeys.length ? (
            <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 8 }}>
              Not in the tracked scope.
              {canEditScope ? (
                <span style={{ marginLeft: 6 }}>
                  <Button size="small" type="link" style={{ padding: 0, height: "auto" }} onClick={() => trackKeys([keysForElement(picked).element])}>Track it</Button>
                  {keysForElement(picked).type ? <> · <Button size="small" type="link" style={{ padding: 0, height: "auto" }} onClick={() => trackKeys([keysForElement(picked).type])}>track its type</Button></> : null}
                  {keysForElement(picked).category ? <> · <Button size="small" type="link" style={{ padding: 0, height: "auto" }} onClick={() => trackKeys([keysForElement(picked).category])}>its category</Button></> : null}
                </span>
              ) : " Tick its category, family or type in the tree to track it."}
            </div>
          ) : null}
          {/* AREA, with the status (owner, 2026-10-09: "area dropdown like
              issue area drop down, along with status drop down"). This one
              element, by its uniqueId. */}
          {canEdit ? (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 4 }}>AREA</div>
              <AreaTag projectId={projectId} canEdit target={{ count: 1, body: { uniqueIds: [picked.uniqueId] } }} width={170} />
            </div>
          ) : null}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 4 }}>STATUS</div>
            {canEdit ? (
              <Select getPopupContainer={popupInStage}
                size="small"
                style={{ width: "100%" }}
                value={picked.status}
                options={STATUS_ORDER.map((v) => ({
                  value: v,
                  label: (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLOR[v] }} />
                      {STATUS_LABEL[v] || v}
                    </span>
                  ),
                }))}
                onChange={async (v) => {
                  try {
                    const when = dateForStatus(v, statusDate, projectedDate);
                    await setElementStatus(projectId, { uniqueIds: [picked.uniqueId], status: v, date: when });
                    setPicked((prev) => (prev ? { ...prev, status: v, statusDate: (when ? new Date(when) : new Date()).toISOString() } : prev));
                    setStatusByUid((prev) => {
                      const next = new Map(prev);
                      next.set(picked.uniqueId, v);
                      return next;
                    });
                    loadStats();
                    statusWritten();
                  } catch (e) { if (!e.cancelled) message.error(e.message || "Could not set the status"); }
                }}
              />
            ) : (
              <Tag color={picked.status === "installed" ? "green" : undefined}>
                {STATUS_LABEL[picked.status] || picked.status}
              </Tag>
            )}
            {canEdit && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 11.5, color: T.muted, flexWrap: "wrap" }}>
                <span>as of</span>
                <input
                  type="date"
                  value={statusDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setStatusDate(e.target.value)}
                  style={{ fontSize: 12, padding: "1px 4px", border: `1px solid ${T.line}`, borderRadius: 4 }}
                />
                <span title="When a projected install is expected">· projected for</span>
                <input
                  type="date"
                  value={projectedDate}
                  onChange={(e) => {
                    // READ THE EVENT BEFORE THE AWAIT. React 16 pools
                    // synthetic events and nulls `target` as soon as the
                    // handler returns, so the .then() below - which runs
                    // after a network round-trip - read `.value` of null
                    // and took the whole Model view down through its error
                    // boundary: "Cannot read properties of null (reading
                    // 'value')" (owner, on prod, 2026-09-28). Setting a
                    // projected date is the one path that awaited.
                    const when = e.target.value;
                    setProjectedDate(when);
                    // Re-dating a projected element is re-planning it.
                    if (picked && picked.status === "projected" && when) {
                      setElementStatus(projectId, { uniqueIds: [picked.uniqueId], status: "projected", date: when })
                        .then(() => setPicked((prev) => (prev ? { ...prev, statusDate: new Date(when).toISOString() } : prev)))
                        .catch((err) => { if (!err.cancelled) message.error(err.message || "Could not set the date"); });
                    }
                  }}
                  style={{ fontSize: 12, padding: "1px 4px", border: `1px solid ${T.line}`, borderRadius: 4 }}
                />
              </div>
            )}
          </div>
          {bulkNode}
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "6px 0" }}>
            {[
              ["Category", picked.category],
              ["Family", picked.family],
              ["Type", picked.typeName],
              ["Level", picked.level],
              ["Name", picked.name],
              ["Status set", picked.statusDate ? new Date(picked.statusDate).toLocaleDateString() : "—"],
              ["Projected", picked.projectedStart || picked.projectedEnd
                ? `${picked.projectedStart ? new Date(picked.projectedStart).toLocaleDateString() : "?"} → ${picked.projectedEnd ? new Date(picked.projectedEnd).toLocaleDateString() : "?"}`
                : "—"],
              ["Sets", (picked.lists || []).map((l) => l.name).join(", ")],
              ["Unique id", picked.uniqueId],
            ].map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
                  {String(label).toUpperCase()}
                </div>
                <div style={{ fontSize: 12.5, color: T.ink, wordBreak: "break-all" }}>{value || "—"}</div>
              </div>
            ))}
          </div>
          {picked.note ? (
            <div style={{ marginTop: 10, fontSize: 12.5, color: T.ink }}>{picked.note}</div>
          ) : null}
        </>
      )}
    </Card>
  ) : null;

  const overall = stats?.overall;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <Select getPopupContainer={popupInStage}
          mode="multiple"
          allowClear
          size="small"
          style={{ minWidth: 320, maxWidth: 520 }}
          placeholder="All linked files"
          value={documentIds}
          onChange={setDocumentIds}
          maxTagCount="responsive"
          optionFilterProp="label"
          options={documents.map((d) => ({
            value: d.id,
            label: `${d.name}${d.elementCount ? ` · ${d.elementCount.toLocaleString()}` : ""}`,
          }))}
        />
        <Select getPopupContainer={popupInStage}
          size="small"
          style={{ minWidth: 200 }}
          allowClear
          placeholder="Whole model (no set)"
          value={activeList || undefined}
          onChange={(v) => setActiveList(v || "")}
          options={lists.map((l) => ({ value: String(l._id), label: `${l.name} · ${l.count}` }))}
        />
        <Button size="small" icon={<PlusOutlined />} disabled={!checked.length} onClick={() => setListOpen(true)} style={shownIf(canAddSet)}>
          Create a set
        </Button>
        {!!activeList && canDelete && (
          <Tooltip getPopupContainer={popupInStage} title="Remove this set">
            <Button
              size="small" danger icon={<DeleteOutlined />}
              onClick={async () => {
                await deleteList(projectId, activeList).catch((e) => message.error(e.message));
                setActiveList("");
                await loadLists();
              }}
            />
          </Tooltip>
        )}
        <span style={{ flex: 1 }} />
        {canEdit && (
          <Tooltip getPopupContainer={popupInStage} title="Push the projected install dates onto the activities these elements sit under. Extends a bar to cover them; never shrinks one.">
            <Button size="small" icon={<ScheduleOutlined />} onClick={onSyncSchedule}>Update the schedule</Button>
          </Tooltip>
        )}
        <Button size="small" icon={<FileExcelOutlined />} loading={exporting === "xlsx"} onClick={() => runExport("xlsx")}>
          Excel
        </Button>
        <Button size="small" icon={<PrinterOutlined />} loading={exporting === "print"} disabled={!!exporting && exporting !== "print"} onClick={() => runExport("print")}>
          Print
        </Button>
        <Button size="small" icon={<FilePdfOutlined />} loading={exporting === "pdf"} onClick={() => runExport("pdf")}>
          PDF
        </Button>
        <Tooltip getPopupContainer={popupInStage} title="Every element in scope, one row each, with its status, who set it, the note and its quantities - no row limit">
          <Button size="small" icon={<FileTextOutlined />} loading={exporting === "csv"} onClick={() => runExport("csv")}>
            CSV
          </Button>
        </Tooltip>
      </div>

      {/* The numbers, from the model rather than from the rows: an
          untouched project reports every element projected without a
          single status row existing. */}
      {/* ONE ROW. The drawing's totals (tracked / installed / remaining,
          handed down as statsPrefix) and the model's own stand side by
          side; By status takes what is left, which is why it is the one
          that narrows. */}
      {(overall || statsPrefix) && (
        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "stretch" }}>
          {statsPrefix}
          {overall && (
          <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6, padding: "8px 14px", minWidth: 132 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>IN SCOPE</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>{(overall.total || 0).toLocaleString()}</div>
            <div style={{ fontSize: 11, color: T.muted }}>elements</div>
          </div>
          )}
          {overall && (
          <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6, padding: "8px 14px", minWidth: 150 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>INSTALLED</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.ink, lineHeight: 1.25 }}>{overall.percentInstalled}%</div>
            <Progress
              percent={overall.percentProgress} size="small" showInfo={false}
              strokeColor={STATUS_COLOR.installed} style={{ marginTop: 2 }}
            />
            <div style={{ fontSize: 11, color: T.muted }}>{overall.percentProgress}% weighted</div>
          </div>
          )}
          {overall && (
          <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6, padding: "8px 12px", flex: "1 1 240px", minWidth: 200 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 6 }}>BY STATUS</div>
            <div style={{ display: "flex", height: 14, borderRadius: 3, overflow: "hidden" }}>
              {STATUS_ORDER.map((s) => {
                const n = (overall.byStatus || {})[s] || 0;
                const pct = overall.total ? (n / overall.total) * 100 : 0;
                if (!pct) return null;
                return (
                  <Tooltip getPopupContainer={popupInStage} key={s} title={`${STATUS_LABEL[s]}: ${n.toLocaleString()}`}>
                    <div style={{ width: `${pct}%`, background: STATUS_COLOR[s] }} />
                  </Tooltip>
                );
              })}
            </div>
            {/* The key, in short form so it still fits when the card
                gives its width to the cards beside it. */}
            <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              {STATUS_ORDER.map((s) => (
                <Tooltip getPopupContainer={popupInStage} key={s} title={`${STATUS_LABEL[s]}: ${((overall.byStatus || {})[s] || 0).toLocaleString()}`}>
                  <span style={{ fontSize: 11, color: T.muted, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                    <span style={{ width: 9, height: 9, borderRadius: 2, background: STATUS_COLOR[s] }} />
                    {STATUS_SHORT[s] || STATUS_LABEL[s]} {((overall.byStatus || {})[s] || 0).toLocaleString()}
                  </span>
                </Tooltip>
              ))}
            </div>
          </div>
          )}
        </div>
      )}
      {statsLoading && !stats && <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>}

      <div style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "6px 10px",
        background: scopeKeys.length ? "#EEF4FF" : "#FAFAFA", border: `1px solid ${scopeKeys.length ? "#C7DBFF" : T.line}`,
        borderRadius: 6, fontSize: 12.5,
      }}>
        <span>
          {scopeKeys.length
            ? `Tracking ${scopeUids.length.toLocaleString()} element${scopeUids.length === 1 ? "" : "s"} in ${scopeKeys.length} pick${scopeKeys.length === 1 ? "" : "s"}`
              + (scope && scope.canEdit ? (scopeSaving ? " · saving…" : " · remembered for this project") : " · preview in this tab only")
              + ". The whole model draws; the tracked part is coloured by status."
            : "Nothing is tracked yet. Tick the categories, families or types to track in the tree - the whole model draws, and the ticked part is coloured by status."}
          {isolating ? " Working out the page's cut…" : ""}
          {!isolating && isolateUids && cutLevels.length ? ` This page cuts to ${cutLevels.join(", ")}.` : ""}
          {/* A custom category whose parts could not be resolved stays
              hidden while the isolate is on. Named, because the thing a
              person would otherwise do is click it and find nothing. */}
          {derivedPartNote ? ` ${derivedPartNote}` : ""}
          {tickedPartNote ? ` ${tickedPartNote}` : ""}
          {/* The two states a person cannot otherwise account for: an empty
              view he asked for, and a tick that matched nothing. */}
          {hideNote ? ` ${hideNote}` : ""}
        </span>
        <span style={{ flex: 1 }} />
        {/* SAVED IS NOT APPLIED. The ticks autosave, so the data is
            never lost; the MODEL ON SCREEN is what has not caught up.
            Its own component, because the conditional text would
            otherwise overflow the hooks linter's path counter in this
            file - which has cost a build before. */}
        <ScopeApplyBar
          state={scopeApply}
          preview={scopePreview}
          canEdit={canEditScope}
          onApply={onApplyScope}
        />
        {scopeKeys.length ? (
          <Button size="small" onClick={() => { userTouched.current = true; setChecked([]); }}>Track nothing</Button>
        ) : null}
      </div>

      {/* The page bar. Each page is a level's drawing with that level's
          slice of the model over it, so a building is tracked page by
          page rather than by re-aligning one canvas over and over. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        {pages.map((pg) => (
          <Button
            key={pg._id}
            size="small"
            type={String(pg._id) === String(activePageId) ? "primary" : "default"}
            onClick={() => setActivePageId(String(pg._id))}
          >
            {pg.name || "Page"}
            {pg.cut && pg.cut.mode === "level" && (pg.cut.levelNames || []).length
              ? ` · ${pg.cut.levelNames.join(", ")}` : ""}
            {pg.cut && pg.cut.mode === "elevation" ? " · section" : ""}
          </Button>
        ))}
        {canAdd && (
          <Tooltip getPopupContainer={popupInStage} title="A new page: pick its sheet and the level it cuts, then align them">
            <Button size="small" icon={<PlusOutlined />} onClick={async () => {
              try {
                const r = await createPage(projectId, { name: `Page ${pages.length + 1}` });
                setPages((prev) => [...prev, r.page]);
                setActivePageId(String(r.page._id));
              } catch (e) { message.error(e.message || "Could not add a page"); }
            }} />
          </Tooltip>
        )}
        {/* THE PAGE'S LEVELS, where the page is chosen (owner, 2026-09-22).
            One or many; empty = every level. They filter both trees, the
            board, every selection and every status write on this page -
            and cut the model to them, as the section panel's level cut
            always did (it is the same setting, page.cut). */}
        {activePage && (
          <Select getPopupContainer={popupInStage}
            mode="multiple"
            size="small"
            allowClear
            style={{ minWidth: 220, maxWidth: 420 }}
            placeholder="All levels on this page"
            value={cutLevels}
            disabled={!canEdit}
            maxTagCount="responsive"
            optionFilterProp="label"
            options={levels.map((l) => ({ value: l.name, label: `${l.name} · ${Number(l.count || 0).toLocaleString()}` }))}
            onChange={async (levelNames) => {
              try {
                const cut = levelNames.length
                  ? { ...(activePage.cut || {}), mode: "level", levelNames }
                  : { ...(activePage.cut || {}), mode: (activePage.cut && activePage.cut.mode === "level") ? "none" : ((activePage.cut && activePage.cut.mode) || "none"), levelNames: [] };
                const r = await updatePage(projectId, activePage._id, { cut });
                setPages((prev) => prev.map((x) => (String(x._id) === String(r.page._id) ? r.page : x)));
              } catch (e) { message.error(e.message || "Could not set the page's levels"); }
            }}
          />
        )}
        {canDelete && activePage && pages.length > 1 && (
          <Tooltip getPopupContainer={popupInStage} title="Remove this page">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={async () => {
              try {
                await deletePage(projectId, activePage._id);
                setPages((prev) => prev.filter((x) => String(x._id) !== String(activePage._id)));
                setActivePageId("");
              } catch (e) { message.error(e.message || "Could not remove the page"); }
            }} />
          </Tooltip>
        )}
        {!pages.length && (
          <span style={{ fontSize: 12, color: T.muted }}>
            No pages yet. Align the model over a sheet and save, and this becomes your first page.
          </span>
        )}
      </div>

      {scopeNote ? (
        <div style={{ color: "#6B7280", marginBottom: 8 }}>{scopeNote}</div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)", gap: 12, alignItems: "start" }}>
        {scopeReady ? (
        <ModelStage
          projectId={projectId}
          project={project}
          sheets={sheets}
          sheetId={activePage ? String(activePage.sheetDocControlId || "") : ""}
          onSheetChange={async (id, sheetPage = 1) => {
            // Choosing a sheet is a property of the PAGE, so it is saved
            // as one rather than held in the component: switching pages
            // and back must show the same drawing. So is WHICH PAGE of the
            // document - a set holds one floor per page.
            try {
              if (activePage) {
                const r = await updatePage(projectId, activePage._id, { sheetId: id, page: sheetPage });
                setPages((prev) => prev.map((x) => (String(x._id) === String(r.page._id) ? r.page : x)));
              } else {
                const r = await createPage(projectId, { name: "Page 1", sheetId: id, page: sheetPage });
                setPages((prev) => [...prev, r.page]);
                setActivePageId(String(r.page._id));
              }
            } catch (e) { message.error(e.message || "Could not set the sheet"); }
          }}
          documentIds={documentIds}
          overlay={activePage}
          modelTotal={stats && stats.overall ? (stats.overall.total || null) : null}
          levels={levels}
          cut={activePage ? activePage.cut : null}
          onCutChange={async (cut) => {
            try {
              // THE CLEAR HAPPENS HERE, with the cut that caused it, so
              // one write carries both and the page cannot end up with a
              // switch on and its level filter still applied. The RULE
              // lives in cutClearsFilter - a rule inside an onChange
              // handler is a rule no test reaches, and the first version
              // of this shipped with nothing asserting it: deleting the
              // line that cleared the filter left every suite green.
              const clear = clearLevelFilter(activePage && activePage.cut, cut, activePage && activePage.filters);
              if (!activePage) {
                const r = await createPage(projectId, { name: "Page 1", cut });
                setPages((prev) => [...prev, r.page]);
                setActivePageId(String(r.page._id));
                return;
              }
              const r = await updatePage(projectId, activePage._id, {
                cut, ...(clear ? { filters: clear.filters } : {}),
              });
              setPages((prev) => prev.map((x) => (String(x._id) === String(r.page._id) ? r.page : x)));
              if (clear) {
                // RE-SEAT THE HEADER, or the chip keeps showing a level
                // filter that is no longer applied - a screen that lies,
                // which is worse than either option he declined.
                setFilterSeat((n) => n + 1);
                message.info(clear.message, 10);
              }
            } catch (e) { message.error(e.message || "Could not set the cut"); }
          }}
          canEdit={canEdit}
          elevationNote={elevationInfo.note}
          elevationAdded={elevationInfo.added}
          elevationRange={elevationInfo.range}
          onSaveOverlay={stage.current}
          statusByUid={paintByUid}
          scopeUids={scopeUids}
          scopeResolving={scopeResolving}
          isolateUids={(() => {
            // A page's cut and "only the selected" narrow together: the
            // cut says which part of the building, the switch which of
            // its elements.
            const cut = newFocus ? (isolateUids ? newFocus.filter((u) => isolateUids.includes(u)) : newFocus) : isolateUids;
            if (!onlySelected || !onlySelectedUids) return cut;
            if (!cut) return onlySelectedUids;
            const inCut = new Set(cut);
            const both = onlySelectedUids.filter((u) => inCut.has(u));
            return both.length ? both : ["__nothing__"];
          })()}
          isolationRule={onlySelected ? drawRuleWithParts : null}
          // WHAT IS PAINTED, which is NOT the set. Three cases, and the
          // middle one is the whole argument:
          //   Tracking quantities has ticks -> paint exactly those. They
          //     are a selection INSIDE the set, which is the only thing
          //     worth a colour once the set is the only thing drawn.
          //   nothing there, isolate ON       -> paint NOTHING. The set is
          //     already the only geometry on screen; painting all of it
          //     green would say nothing and would hide what was picked
          //     (leader, 2026-09-20 - the complaint that started this).
          //   nothing there, isolate OFF      -> paint the ticks. In the
          //     full model a tick has no other way to show itself, which
          //     was the owner's "ticking a category highlights nothing".
          // A plain click needs no rule at all: highlightUids carries it
          // and root-f5's store ORs the two.
          highlightRule={filterLit ? null : (innerRuleWithParts || (onlySelected ? null : selectionRule))}
          // The filter hides; hand-hides still win and clearing restores.
          hiddenRule={filterHiddenRule}
          // The second tab under the model column: everything about what
          // is selected, and the quantities under it. It reads the SAME
          // summary the bulk bar's count comes from, so the two cannot
          // disagree - it is one call, not two that agree.
          detailsLabel={activeSet ? activeSet.name : ""}
          // The count under the name stays the ONE number the bulk bar and
          // the details panel share; with nothing ticked it falls back to
          // the set's own size, so the tab never reads "0" while a set of
          // 40,000 is open.
          detailsCount={barCount ? barCount.n : (activeSet ? activeSet.count : null)}
          details={(
            <SelectionDetails
              projectId={projectId}
              refreshKey={writeSeq}
              // CUSTOM PART CATEGORIES, reachable with nothing selected.
              trackedCategories={trackedCategories}
              onLoadPartGroups={loadPartGroups}
              onPreviewPartCategory={previewPartCategory}
              onCreateSubcategory={createPartCategory}
              canEdit={canEditScope}
              // SHOW ONLY FILTERED (owner, 2026-09-28: "also add a toggle on top
              // that says show only filtered", "which checks elements filtered in
              // the quantity tracking"). It renders at the TOP OF THE PANEL, and
              // it is tied to the QUANTITY TRACKING filter alone - never a union
              // of the page picker, the model ticks and this one, because a
              // toggle that silently unions two sources is the same class of bug
              // as a filter that silently becomes a selection.
              // Passed in rather than wrapped around the panel: `details` has to
              // stay ONE element, which selectionRace.test.js reads through.
              filteredOnly={filteredOnly}
              hasQuantityFilter={!!quantityFilters}
              onFilteredOnly={setFilteredOnly}
              selection={serverSelection}
              selectionCount={barCount ? barCount.n : null}
              // WHAT THE RECTANGLE HAS CAUGHT SO FAR, while it is still
              // being drawn. Null the moment the drag ends, so the panel
              // goes back to describing a finished selection rather than
              // a moving one.
              liveCount={liveCount}
              // ...AND WHETHER RELEASING WILL ACTUALLY SHOW THEM. With
              // select-all-filtered on, baseSelectionRaw ignores `selected`
              // entirely - the filter IS the selection - so a box cannot
              // reach this panel at all. That branch is not mine to change
              // tonight (the owner is deciding whether a deliberate
              // select-all or a deliberate box should win), but the panel
              // must not print "release to see them" and then not show
              // them. It says what will happen instead.
              liveCountApplies={!(allFiltered && quantityFilters)}
              documentIds={prefixFilter ? prefixFilter.split(",") : []}
              // THE SELECTION DOCUMENT the box path already creates
              // (onBoxSelect -> createQuantitySelection). It survived
              // being taken out of the quantities FILTER - the document
              // still exists, the table simply stopped narrowing by it -
              // so the per-element list can page through it server-side
              // rather than this page carrying every id.
              selectionId={boxSel ? boxSel.id : null}
              // The same refresh the bulk bar does after a write, so the
              // board, the colours and the counters do not sit stale
              // behind a list that has just changed them.
              onStatusWritten={() => { loadTracked(); loadStats(); statusWritten(); }}
              summary={selSummary}
              quantitiesState={quantitiesState}
              levelPick={levelPick}
              onLevelPick={setLevelPick}
              levelPickBusy={levelBusy}
              levelPickNote={levelNote}
            />
          )}
          onlySelected={onlySelected}
          onOnlySelected={setOnlySelected}
          onlySelectedBusy={onlySelectedBusy}
          onlySelectedCount={(onlySelectedUids || []).filter((u) => u !== "__nothing__").length}
          onlySelectedReady={drawKeys.length > 0}
          onIsolationTakenOver={() => { if (onlySelected) { pauseOnlySelected(); message.info("The viewer's isolate [I] and this switch answer the same question, so [I] wins: \"Tracked elements\" is off."); } }}
          onPickElement={pickElement}
          onBoxSelect={onBoxSelect}
          onSelectionChanged={onSelectionChanged}
          // What was hidden stops being the selection, as in the viewer:
          // a selection you cannot see is one the next command acts on.
          onHidden={() => { setSelected([]); setBoxSel(null); }}
          panel={panelNode}
          touchedByUid={statusByUid}
          highlightUids={selectionUids}
          selectionUids={selectionUids}
          autoFrame={autoFrame}
          onAutoFrame={setAutoFrame}
          // A filter or a gate rule applied FRAMES EVERYTHING IT MATCHED,
          // every time it is run - not only when the match is off screen.
          // Ticking rows inside it frames the ordinary way ('auto'), so a
          // person ticking is not dragged about.
          frameAll={filterLit && qfOwn && !innerKeys.length}
          frameKey={filterLit && qfOwn && !innerKeys.length ? `${quantityFiltersStamp}:${writeSeq}` : ""}
          focusUids={focus.uids}
          focusKey={focus.key}
        />
        ) : (
          // THE STAGE IS HELD, NOT BLANK. Holding it silently is how a
          // slow scope call becomes "the viewer is broken"; the budget
          // in useTrackingScope bounds this to a few seconds and then
          // draws unfiltered rather than waiting for ever.
          <div style={{ color: "#6B7280", padding: 12 }}>Reading the tracking scope…</div>
        )}
        <Card
          size="small"
          title="Model elements"
          bodyStyle={{ padding: 10 }}
          extra={checked.length ? (
            // The bulk bar: what is ticked, and what can be done to all of
            // it at once. The bar itself is shared with the Detailed tree
            // and the diff list (./BulkBar) so the three read the same way,
            // in the same order. Status changes go through the preview /
            // confirm / undo path, never straight to a write.
            <BulkBar
              count={barCount ? barCount.n : null}
              countNote={barCount && barCount.atLeast ? `more than ${barCount.n.toLocaleString()} elements` : ""}
              selections={minimalKeys(checked).length}
              canEdit={canEdit}
              busy={bulkBusy}
              // This bar acts on THE SET - the ticks of this tree - which
              // is what it has always done and is not the shared
              // selection. Named in the confirmation so the two bars can
              // never be confused for one another.
              onStatus={(key) => { setBulkStatus(key); applyBulk({ nodeKeys: minimalKeys(checked) }, key, { what: "ticked in Model elements" }); }}
              actsOn="everything ticked in Model elements"
              isolated={onlySelected}
              onIsolate={() => setOnlySelected(!onlySelected)}
              onAddToList={whenAllowed(canAddSet, () => setListOpen(true))}
              // The same picker the right-hand panel carries, from where the
              // owner looked for it. One component, two ways in - if this
              // ever grows its own copy of the create call it has gone wrong.
              onCustomCategory={() => setPartsOpen(true)}
              undoBusy={undoBusy}
              onUndo={async () => {
                setUndoBusy(true);
                try {
                  const u = await undoElementStatus(projectId);
                  message.success(`Undone: ${Number(u.undone || 0).toLocaleString()} element${u.undone === 1 ? "" : "s"} put back`);
                  loadTracked(); loadStats(); statusWritten();
                } catch (e) {
                  message.info(e.status === 404 ? "There is nothing of yours left to undo" : (e.message || "Could not undo"));
                } finally { setUndoBusy(false); }
              }}
              onClear={() => setChecked([])}
            />
          ) : null}
        >
          <ElementTree
            projectId={projectId}
            documentIds={documentIds}
            pageLevels={cutLevels}
            cutUids={cutUidsId}
            canEdit
            checkedKeys={checked}
            statusByUid={statusByUid}
            statusDate={statusDate}
            projectedDate={projectedDate}
            onSelectParts={onSelectParts}
            onDerivedTicks={(ids) => { userTouched.current = true; setDerivedIds(ids); }}
            onSetChecked={(keys) => { userTouched.current = true; setChecked(keys); }}
            onCheck={(keys, info, loadedKeys) => {
              userTouched.current = true;
              // What the tree reported, plus the saved picks it has not
              // loaded and so could not report - see scopeKeys.js.
              setChecked((prev) => {
                const next = mergeCheckedKeys(prev, keys, loadedKeys || []);
                // The camera is NOT jumped to whatever was ticked last
                // (owner, 2026-09-20): the whole selection is highlighted
                // and Auto-frame grows the view to hold all of it, so
                // ticking a second category widens the shot instead of
                // throwing the person at the newest row.
                return next;
              });
            }}
            onStatusChanged={({ node, status, undone }) => {
              // The element case is applied here so the model recolours
              // on the same tap. A section case moved elements this
              // client never listed, so that one re-reads (as does an undo).
              if (node.level === "element" && !undone) {
                setStatusByUid((prev) => {
                  const next = new Map(prev);
                  next.set(node.uniqueId, status);
                  return next;
                });
              } else {
                loadTracked();
              }
              statusWritten();
              loadStats();
            }}
            height={430}
          />
        </Card>
        {/* Below both panels, the full width of the page: what the last
            publish added, and every element with its amounts. */}
        <Card size="small" style={{ gridColumn: "1 / -1" }} bodyStyle={{ padding: "4px 12px 12px" }}>
          <Tabs
            size="small"
            // TRACKING QUANTITIES IS THE FIRST TAB AND THE DEFAULT (owner,
            // 2026-09-20). It is the tab the field works from, so it opens
            // on it rather than on what the last publish happened to add.
            defaultActiveKey="quantities"
            items={[
              {
                key: "quantities",
                label: "Tracking quantities",
                children: (
                  <QuantityTree
                    projectId={projectId}
                    canSaveRules={canAddSet}
                    checked={recordChecked}
                    onChecked={(keys) => {
                      // A TICK IN THIS TABLE IS THE DELIBERATE ACT. The three
                      // other places that clear recordChecked are side
                      // effects - a model click, selecting a custom
                      // category's parts, creating a set - and flagging those
                      // would blank the viewer on a click.
                      setQuantityTouched(true);
                      setRecordChecked(keys);
                    }}
                    excluded={excluded}
                    onExcluded={setExcluded}
                    // The other two panels' half of "2 way": what was
                    // clicked in the model and what level was picked in
                    // the details panel, marked where this tree shows it.
                    selectedUids={pickedSet}
                    selectedLevels={levelPick}
                    onFiltersChange={onQuantityFilters}
                    // The page remembers its filter; the key is the page, so
                    // switching pages re-seats the headers and nothing else does.
                    savedFilters={activePage ? activePage.filters : null}
                    savedFiltersKey={`${String(activePageId || "")}:${filterSeat}`}
                    listId={activeList}
                    pageLevels={cutLevels}
                    cutUids={cutUidsId}
                    selectionId={boxSel ? boxSel.id : ""}
                    selectionCount={boxSel ? boxSel.count : 0}
                    onClearSelection={() => { setSelected([]); setBoxSel(null); }}
                    listName={activeSet ? activeSet.name : ""}
                    refreshKey={treeSeq}
                    allFiltered={allFiltered}
                    onAllFiltered={setAllFiltered}
                    // ONE NUMBER: the same summary the bulk bar counts
                    // from, so the checkbox cannot say 1,240 while the
                    // button writes something else.
                    allFilteredCount={allFiltered && barCount ? barCount.n : null}
                    // The SAME bar as Model elements, with the same words in
                    // the same order (./BulkBar) - only what a tick resolves
                    // to differs, because this tree's rows are record rows.
                    bulkBar={(
                      <BulkBar
                        count={barCount ? barCount.n : null}
                        countNote={barCountNote}
                        selections={minimalRecordKeys(recordChecked).length}
                        canEdit={canEdit}
                        busy={bulkBusy}
                        // THE SELECTION, not this tree's ticks alone: a
                        // level picked in the details panel and an element
                        // clicked in the model are part of it, and "pick
                        // Level 2, press Installed" has to mark Level 2
                        // (owner, 2026-09-20). The confirmation names what
                        // it is about to change.
                        onStatus={(key) => { setBulkStatus(key); applyBulk(serverSelection || nodesForKeys(recordChecked), key, { what: selectionWords }); }}
                        actsOn={bulkActsOn}
                        isolated={onlySelected}
                        onIsolate={() => setOnlySelected(!onlySelected)}
                        // NOT OFFERED FOR A SELECT-ALL, rather than offered
                        // and wrong: "Create a set" saves ticked rows, and a
                        // select-all has none - it would save the wrong set
                        // (or nothing) while the bar reads "12 elements".
                        onAddToList={whenAllowed(canAddSet && !allFiltered, () => setListOpen(true))}
                        undoBusy={undoBusy}
                        onUndo={async () => {
                          setUndoBusy(true);
                          try {
                            const u = await undoElementStatus(projectId);
                            message.success(`Undone: ${Number(u.undone || 0).toLocaleString()} element${u.undone === 1 ? "" : "s"} put back`);
                            loadTracked(); loadStats(); statusWritten();
                          } catch (e) {
                            message.info(e.status === 404 ? "There is nothing of yours left to undo" : (e.message || "Could not undo"));
                          } finally { setUndoBusy(false); }
                        }}
                        // Clear means clear: the select-all too, or the
                        // bar would vanish while the selection stood.
                        onClear={() => {
                          // Pressing Clear in the quantities bulk bar is also
                          // his act, so it counts as touched: clearing to
                          // nothing means nothing shown.
                          setQuantityTouched(true);
                          setRecordChecked([]);
                          setAllFiltered(false);
                        }}
                      />
                    )}
                    scopeStamp={scope ? `${scope.updatedAt || ""}:${(scope.nodeKeys || []).length}` : ""}
                    documentIds={prefixFilter ? prefixFilter.split(",") : []}
                    canEdit={canEdit}
                    statusByUid={statusByUid}
                    statusDate={statusDate}
                    projectedDate={projectedDate}
                    onSelectElement={pickElement}
                    onStatusChanged={(uids, status) => {
                      // A node change past 50k elements (or an undo) comes
                      // back without ids: read the statuses again.
                      if (uids) {
                        setStatusByUid((prev) => {
                          const next = new Map(prev);
                          uids.forEach((u) => next.set(u, status));
                          return next;
                        });
                      } else {
                        loadTracked();
                      }
                      loadStats();
                      // This tree reads itself again; the filter's lit
                      // set follows the write.
                      statusWritten({ tree: false });
                    }}
                  />
                ),
              },
              {
                key: "new",
                label: "New since last publish",
                children: (
              <NewSincePublish
                projectId={projectId}
                onAddToList={whenAllowed(canAddSet, (uids) => { setListSeed(uids || []); setListOpen(true); })}
                undoBusy={undoBusy}
                onUndo={async () => {
                  setUndoBusy(true);
                  try {
                    const u = await undoElementStatus(projectId);
                    message.success(`Undone: ${Number(u.undone || 0).toLocaleString()} element${u.undone === 1 ? "" : "s"} put back`);
                    loadTracked(); loadStats(); statusWritten();
                  } catch (e) {
                    message.info(e.status === 404 ? "There is nothing of yours left to undo" : (e.message || "Could not undo"));
                  } finally { setUndoBusy(false); }
                }}
                documentIds={prefixFilter ? prefixFilter.split(",") : []}
                canEdit={canEdit}
                canEditScope={canEditScope}
                statusDate={statusDate}
                projectedDate={projectedDate}
                onProjectedDateChange={setProjectedDate}
                statusByUid={statusByUid}
                focused={!!newFocus}
                onFocus={setNewFocus}
                onHighlight={focusOn}
                onTrackKeys={trackKeys}
                onStatusChanged={(uids, status) => {
                  if (uids) {
                    setStatusByUid((prev) => {
                      const next = new Map(prev);
                      uids.forEach((u) => next.set(u, status));
                      return next;
                    });
                  } else {
                    loadTracked();
                  }
                  loadStats();
                  statusWritten();
                }}
              />
                ),
              },
            ]}
          />
        </Card>
      </div>

      {/* A CUSTOM CATEGORY MADE OF ASSEMBLY PARTS. The picker, the part
          groups endpoint, the definition and the create call all shipped
          2026-09-24/10-02; this is the second door onto them, because the
          first one was a collapsed header in a side panel and the owner
          never found it. destroyOnClose so each open asks the server
          again - a part catalogue changes with the publish. */}
      <Modal getContainer={stageContainer}
        open={partsOpen}
        title="Create a custom category from assembly parts"
        footer={null}
        width={760}
        onCancel={() => setPartsOpen(false)}
        destroyOnClose
      >
        <PartCategoryPicker
          projectId={projectId}
          categories={trackedCategories}
          documentIds={prefixFilter}
          loadGroups={loadPartGroups}
          onCreate={async (...args) => {
            const r = await createPartCategory(...args);
            // STAYS OPEN, and the list below shows what was just created.
            // Closing on create left the owner with a success toast and
            // nothing to click, which is how the feature was invisible in
            // the first place.
            setPartsMade((v) => v + 1);
            return r;
          }}
          onPreview={previewPartCategory}
          canEdit={allOf(canEditScope, canAdd)}
        />
        <div style={{ marginTop: 16, borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 2 }}>Custom categories on this project</div>
          <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 6 }}>
            Selecting one picks its parts in the model, and the bulk bar, Isolate and Set status then work on them as on anything else.
          </div>
          <CustomCategoryList
            projectId={projectId}
            documentIds={prefixFilter}
            reloadKey={partsMade}
            canEdit={allOf(canEditScope, canDelete)}
            onChanged={() => setPartsMade((v) => v + 1)}
            onSelect={onSelectParts}
          />
        </div>
      </Modal>

      <Modal getContainer={stageContainer}
        open={listOpen}
        title="Create a set"
        okText="Create"
        onOk={onCreateList}
        onCancel={() => { setListOpen(false); setListSeed([]); }}
        destroyOnClose
      >
        <p style={{ fontSize: 12.5, color: T.muted }}>
          {listSeed.length
            ? "These are the elements the last publish added, so the set holds those elements themselves - the next publish redefines what is new, and this set will not follow it."
            : "A set remembers what you picked, not only the elements it resolves to today, so it can be re-resolved after the next model is published."}
        </p>
        <Input
          autoFocus
          placeholder="e.g. Level 2 rough-in"
          value={listName}
          onChange={(e) => setListName(e.target.value)}
          onPressEnter={onCreateList}
        />
        <div style={{ marginTop: 8 }}>
          {listSeed.length ? <Tag>{listSeed.length.toLocaleString()} element{listSeed.length === 1 ? "" : "s"} from the last publish</Tag> : null}
          {[...checked, ...recordChecked].slice(0, 8).map((k) => <Tag key={k} style={{ marginBottom: 4 }}>{String(k).split(":").pop()}</Tag>)}
          {checked.length + recordChecked.length > 8 && <Tag>+{checked.length + recordChecked.length - 8} more</Tag>}
        </div>
      </Modal>
    </div>
  );
}
